import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, API } from "../../lib/interview/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Progress } from "../../components/ui/progress";
import { Checkbox } from "../../components/ui/checkbox";
import { Logo, Overline, StatCard } from "../../components/himt/Primitives";
import { fmtIST, fmtISTTime, relTime, toISTDatetimeLocal, fromISTDatetimeLocal } from "../../lib/interview/time";
import { toast } from "sonner";
import {
  Plus, ClipboardList, UsersRound, Trophy,
  Copy, Trash2, Pencil, Mail, Link as LinkIcon, Bot, Upload, Download,
  CheckCircle2, Activity, ListChecks, Library, X, Wifi, WifiOff, Ban, FileSpreadsheet, FileText,
  Cloud, CloudOff, HardDrive, Home, MapPinOff, Info, ChevronDown, ChevronUp, ShieldCheck,
} from "lucide-react";

const emptyTest = {
  title: "",
  description: "",
  question_count: 5,
  ttr_seconds: 5,
  answer_duration_seconds: 30,
  total_duration_minutes: 30,
  proctoring_enabled: true,
  batches: [],
  scheduled_start: "",
  scheduled_end: "",
  question_selections: [],      // [{bank_id, count}]
  specific_question_ids: [],    // pinned ids
  // ---- Combined exam (MCQ + Oral) ----
  exam_mode: "oral",            // "oral" | "combined"
  phase_order: "mcq_first",     // "mcq_first" | "oral_first"
  mcq_bank_ids: [],
  mcq_question_count: 10,
  mcq_duration_minutes: 15,
  mcq_negative_marking: 0,
  mcq_weight: 50,
  oral_weight: 50,
  screen_scan_interval_minutes: 2,
  proctoring_config: {
    id_ocr: true,
    env_scan_360: true,
    webcam_recording: true,
    screen_recording: true,
    tab_switch_detection: true,
    fullscreen_lockdown: true,
    gaze_detection: true,
    virtual_camera_detection: true,
    audio_fingerprinting: true,
  },
};

const PROCTORING_LABELS = {
  id_ocr: "ID Card OCR Verification",
  env_scan_360: "360° Environment Scan",
  webcam_recording: "Webcam Recording",
  screen_recording: "Screen Recording",
  tab_switch_detection: "Tab-switch Detection",
  fullscreen_lockdown: "Fullscreen Lockdown",
  gaze_detection: "Gaze / Focus Detection",
  virtual_camera_detection: "Virtual Camera Detection",
  audio_fingerprinting: "Audio Fingerprinting",
};

const toDatetimeLocal = (iso) => toISTDatetimeLocal(iso);
const fromDatetimeLocal = (local) => fromISTDatetimeLocal(local);

const emptyQuestion = { text: "", key_concepts: [], ideal_answer: "", max_score: 10 };

// ---------- Storage Health Pill ----------
// Polls /admin/health/storage every 30s. Turns amber when uploads have had
// to fall back to local disk — signal to admins that Emergent Object
// Storage is flaking and the exam portal is running on the safety net.
function StorageHealthPill() {
  const [h, setH] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await adminApi().get("/admin/health/storage");
        if (!cancelled) setH(data);
      } catch (e) { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (!h) return null;
  const degraded = h.status === "degraded";
  const title = degraded
    ? `Object Storage is flaky — ${h.pending_local_files} file(s) saved to local disk. Email support@emergent.sh.`
    : `Object Storage healthy — ${h.cloud_writes} cloud uploads this session.`;
  return (
    <div
      title={title}
      data-testid="storage-health-pill"
      className={[
        "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono border",
        degraded
          ? "bg-amber-50 border-amber-300 text-amber-800"
          : "bg-emerald-50 border-emerald-300 text-emerald-800",
      ].join(" ")}
    >
      {degraded ? <CloudOff size={12} /> : <Cloud size={12} />}
      <span className="uppercase tracking-wider">
        Storage: {degraded ? "Degraded" : "OK"}
      </span>
      {degraded && h.pending_local_files > 0 && (
        <span className="ml-1 bg-amber-200 text-amber-900 px-1.5 rounded-sm">
          {h.pending_local_files}
        </span>
      )}
    </div>
  );
}

// Google Drive connection pill — shows whether an admin has connected their
// Workspace Drive as the durable storage backend. Clicking launches the
// OAuth flow (when disconnected) or shows quota (when connected).
function DrivePill() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migProgress, setMigProgress] = useState(null);
  const fetchStatus = async () => {
    try {
      const { data } = await adminApi().get("/admin/drive/status");
      setD(data);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => {
    fetchStatus();
    const u = new URL(window.location.href);
    if (u.searchParams.get("drive") === "connected") {
      toast.success("Google Drive connected. Uploads will now land there.");
      u.searchParams.delete("drive");
      window.history.replaceState({}, "", u.toString());
    } else if (u.searchParams.get("drive") === "error") {
      const msg = decodeURIComponent(u.searchParams.get("msg") || "unknown");
      toast.error("Drive connection failed: " + msg, { duration: 30_000 });
      u.searchParams.delete("drive");
      u.searchParams.delete("msg");
      window.history.replaceState({}, "", u.toString());
    }
    const id = setInterval(fetchStatus, 60_000);
    return () => clearInterval(id);
  }, []);

  // ----- OAuth credential state -------------------------------------------
  const [saOpen, setSaOpen] = useState(false);
  const [oauthClientId, setOauthClientId] = useState("");
  const [oauthClientSecret, setOauthClientSecret] = useState("");
  const [credSaved, setCredSaved] = useState(false);
  const [credLoading, setCredLoading] = useState(false);

  const openModal = async () => {
    setSaOpen(true);
    // Pre-fill saved Client ID (never expose secret)
    try {
      const { data } = await adminApi().get("/admin/drive/oauth-credentials");
      if (data.client_id) setOauthClientId(data.client_id);
      if (data.configured) setCredSaved(true);
    } catch (_e) {}
  };

  const saveCredentials = async () => {
    if (!oauthClientId.trim() || !oauthClientSecret.trim()) {
      return toast.error("Both Client ID and Client Secret are required");
    }
    setCredLoading(true);
    try {
      await adminApi().post("/admin/drive/oauth-credentials", {
        client_id: oauthClientId.trim(),
        client_secret: oauthClientSecret.trim(),
      });
      setCredSaved(true);
      toast.success("Credentials saved. You can now click Login with Google.");
    } catch (e) {
      toast.error("Failed to save credentials: " + (e?.response?.data?.detail || e.message));
    } finally {
      setCredLoading(false);
    }
  };

  const connect = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/drive/connect");
      if (data.redirect_uri) {
        const proceed = window.confirm(
          "About to redirect you to Google sign-in.\n\n" +
          "The redirect URI we are sending to Google is:\n\n" +
          data.redirect_uri + "\n\n" +
          "This EXACT string must be in your Google Cloud Console → Credentials → " +
          "OAuth 2.0 Client → Authorized redirect URIs.\n\nClick OK to continue."
        );
        if (!proceed) { setLoading(false); return; }
      }
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error("Could not start Drive OAuth: " + (e?.response?.data?.detail || e.message));
      setLoading(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? New uploads will go back to Emergent storage / local disk.")) return;
    try {
      await adminApi().post("/admin/drive/disconnect");
      toast.success("Google Drive disconnected.");
      fetchStatus();
    } catch (_e) {
      toast.error("Disconnect failed");
    }
  };

  // ----- Service Account upload -------------------------------------------
  const [saFile, setSaFile] = useState(null);
  const [saFolderId, setSaFolderId] = useState("");
  const [saLoading, setSaLoading] = useState(false);
  const submitServiceAccount = async () => {
    if (!saFile) return toast.error("Please select the service account JSON file");
    if (!saFolderId.trim()) return toast.error("Please paste the Drive folder ID");
    setSaLoading(true);
    try {
      const fd = new FormData();
      fd.append("key_file", saFile);
      const { data } = await adminApi().post(
        `/admin/drive/service-account?folder_id=${encodeURIComponent(saFolderId.trim())}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Connected as ${data.email}. Folder: ${data.folder_id}`);
      setSaOpen(false);
      setSaFile(null);
      setSaFolderId("");
      fetchStatus();
    } catch (e) {
      toast.error("Service account connection failed: " + (e?.response?.data?.detail || e.message), { duration: 15_000 });
    } finally {
      setSaLoading(false);
    }
  };

  if (!d) return null;
  if (!d.connected) {
    return (
      <>
        <button
          onClick={openModal}
          data-testid="connect-drive-btn"
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono border bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100"
          title="Connect Google Drive as storage backend for exam recordings."
        >
          <HardDrive size={12} />
          <span className="uppercase tracking-wider">Connect Drive</span>
        </button>
        {d.last_connect_error && (
          <button
            onClick={openModal}
            data-testid="drive-last-error"
            title={d.last_connect_error}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono border bg-red-50 border-red-300 text-red-700 hover:bg-red-100 max-w-[420px]"
          >
            <CloudOff size={12} className="shrink-0" />
            <span className="truncate">Last connect failed — click to see why</span>
          </button>
        )}

        {saOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setSaOpen(false); }}
            data-testid="sa-dialog"
          >
            <div className="bg-white rounded-sm max-w-xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="font-display font-black text-xl text-[#0A2540] mb-1">Connect Google Drive</h3>
              <div className="text-xs font-mono text-slate-500 mb-4">Store exam recordings in your personal Google Drive.</div>

              {d.last_connect_error && (
                <div className="mb-4 border border-red-300 bg-red-50 rounded-sm p-3" data-testid="drive-error-banner">
                  <div className="text-[11px] font-bold text-red-700 uppercase tracking-wider mb-1">⚠ Last connect attempt failed</div>
                  <div className="text-[12px] text-red-800 break-words leading-snug">{d.last_connect_error}</div>
                  {Array.isArray(d.granted_scopes) && (
                    <div className="text-[11px] text-red-600 mt-1 break-all">Scopes Google granted: {d.granted_scopes.join(", ") || "none"}</div>
                  )}
                  <div className="text-[11px] text-slate-600 mt-2 leading-snug">
                    If it says "did not grant any Drive scope": in Google Cloud Console → <b>APIs &amp; Services → Enabled APIs</b> enable <b>Google Drive API</b>, and in <b>OAuth consent screen → Data Access</b> add the scope <span className="font-mono">.../auth/drive.file</span>, then retry.
                  </div>
                </div>
              )}

              {/* Option 1: OAuth */}
              <div className="mb-5 border border-slate-200 rounded-sm p-4">
                <div className="font-bold text-[#0A2540] text-sm mb-1">Login with Google (OAuth)</div>
                <div className="text-[12px] text-slate-600 mb-3">
                  Works on any Google account including personal Gmail.
                  Enter your Google Cloud OAuth credentials below once, then click Login.
                </div>

                <div className="space-y-3 mb-3">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={oauthClientId}
                      onChange={(e) => { setOauthClientId(e.target.value); setCredSaved(false); }}
                      placeholder="173629076213-xxxx.apps.googleusercontent.com"
                      className="w-full text-xs border border-slate-300 rounded-sm p-2 font-mono"
                      data-testid="oauth-client-id-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">
                      Client Secret
                    </label>
                    <input
                      type="password"
                      value={oauthClientSecret}
                      onChange={(e) => { setOauthClientSecret(e.target.value); setCredSaved(false); }}
                      placeholder="GOCSPX-…"
                      className="w-full text-xs border border-slate-300 rounded-sm p-2 font-mono"
                      data-testid="oauth-client-secret-input"
                    />
                  </div>
                  <Button
                    onClick={saveCredentials}
                    disabled={credLoading || (!oauthClientId.trim() || !oauthClientSecret.trim())}
                    variant="outline"
                    className="w-full rounded-sm text-xs"
                    data-testid="save-credentials-btn"
                  >
                    {credLoading ? "Saving…" : credSaved ? "✓ Credentials Saved" : "Save Credentials"}
                  </Button>
                </div>

                <Button
                  onClick={connect}
                  disabled={loading || !credSaved}
                  className="bg-[#2563EB] text-white rounded-sm w-full"
                  data-testid="oauth-connect-btn"
                >
                  {loading ? "Opening Google…" : "Login with Google →"}
                </Button>
                {!credSaved && (
                  <p className="text-[11px] text-amber-700 mt-1">Save credentials first to enable this button.</p>
                )}

                {/* Exact redirect URI the admin MUST register in Google Cloud
                    Console → Credentials → OAuth Client → Authorized redirect
                    URIs. Computed from the CURRENT origin so it's always right
                    for whatever domain the dashboard is open on (fixes the
                    Error 400: redirect_uri_mismatch after a domain change). */}
                <div className="mt-3 border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="oauth-redirect-uri-box">
                  <div className="text-[11px] font-bold text-amber-800 uppercase tracking-wider mb-1">
                    ⚠ Authorized redirect URI
                  </div>
                  <div className="text-[12px] text-amber-900 mb-2 leading-snug">
                    Add this <b>EXACT</b> URL to your Google Cloud Console → APIs &amp; Services → Credentials → your OAuth Client → <b>Authorized redirect URIs</b>, then retry. Mismatch here is the cause of <span className="font-mono">Error 400: redirect_uri_mismatch</span>.
                  </div>
                  <div className="flex items-center gap-2">
                    <code
                      className="flex-1 text-[12px] font-mono bg-white border border-amber-300 rounded-sm px-2 py-1.5 break-all text-slate-800"
                      data-testid="oauth-redirect-uri-value"
                    >
                      {`${window.location.origin}/api/admin/drive/callback`}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm text-xs whitespace-nowrap border-amber-400 text-amber-800 hover:bg-amber-100"
                      data-testid="copy-redirect-uri-btn"
                      onClick={() => {
                        const uri = `${window.location.origin}/api/admin/drive/callback`;
                        navigator.clipboard?.writeText(uri).then(
                          () => toast.success("Redirect URI copied. Paste it into Google Cloud Console."),
                          () => toast.message(uri),
                        );
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </div>

              <div className="text-xs text-slate-400 text-center mb-3 font-mono">— OR —</div>

              {/* Option 2: Service Account */}
              <div className="border border-slate-200 rounded-sm p-3">
                <div className="font-bold text-[#0A2540] text-sm mb-1">Service Account (Workspace with Shared Drives only)</div>
                <div className="text-[12px] text-slate-600 mb-3">
                  Upload a service-account JSON key + shared Drive folder ID.
                  <b> Does NOT work on personal Gmail.</b>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">JSON Key</label>
                    <input
                      type="file"
                      accept="application/json,.json"
                      onChange={(e) => setSaFile(e.target.files?.[0] || null)}
                      className="w-full text-sm border border-slate-300 rounded-sm p-2"
                      data-testid="sa-file-input"
                    />
                    {saFile && <div className="text-xs font-mono text-emerald-600 mt-1">✓ {saFile.name}</div>}
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1">Shared Drive Folder ID</label>
                    <input
                      type="text"
                      value={saFolderId}
                      onChange={(e) => setSaFolderId(e.target.value)}
                      placeholder="1A2B3CdEf_folder_id"
                      className="w-full text-sm border border-slate-300 rounded-sm p-2 font-mono"
                      data-testid="sa-folder-input"
                    />
                  </div>
                  <Button
                    onClick={submitServiceAccount}
                    disabled={saLoading}
                    variant="outline"
                    className="w-full rounded-sm"
                    data-testid="sa-submit-btn"
                  >
                    {saLoading ? "Verifying…" : "Connect via Service Account"}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end mt-5">
                <Button variant="ghost" onClick={() => setSaOpen(false)} className="rounded-sm" data-testid="sa-cancel-btn">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
  // Connected — show quota pill + test-upload button.
  const used = d.usage_bytes ? Math.round(d.usage_bytes / 1e9 * 10) / 10 : 0;
  const total = d.limit_bytes ? Math.round(d.limit_bytes / 1e9) : null;
  const testUpload = async () => {
    const id = toast.loading("Uploading test file to Drive…");
    try {
      const { data } = await adminApi().post("/admin/drive/test-upload");
      toast.success(
        `✅ Test upload OK (${data.size}B, id ${data.drive_file_id?.slice(-8)}). Check your HIMT Exam Recordings folder.`,
        { id, duration: 15_000 },
      );
    } catch (e) {
      toast.error(
        "❌ " + (e?.response?.data?.detail || e.message || "unknown error"),
        { id, duration: 20_000 },
      );
    }
  };
  // Push every recording that currently lives only in Object Storage / local
  // disk up to Google Drive (recovers files captured while Drive was
  // disconnected). Background job — poll for progress.
  const syncToDrive = async () => {
    if (!window.confirm(
      "Push ALL existing exam files (recordings, ID cards, scans) that aren't yet in Google Drive UP to Drive?\n\n" +
      "• Already-synced files are skipped.\n" +
      "• Safe to repeat.\n• Runs in the background — you can keep working.\n\nContinue?"
    )) return;
    setMigrating(true);
    try {
      const { data } = await adminApi().post("/admin/drive/migrate");
      if (data.ok === false && data.code === "already_running") {
        toast.message("A Drive sync is already running.");
      } else {
        toast.success(`Drive sync started for ${data.queued} file(s).`, { duration: 6000 });
      }
      // Poll status until done.
      const poll = setInterval(async () => {
        try {
          const { data: s } = await adminApi().get("/admin/drive/migrate/status");
          setMigProgress(s);
          if (s.status !== "running") {
            clearInterval(poll);
            setMigrating(false);
            if (s.status === "done") {
              toast.success(
                `Drive sync complete · ${s.uploaded || 0} uploaded, ${s.skipped || 0} skipped, ${s.failed || 0} failed.`,
                { duration: 12000 },
              );
            }
          }
        } catch (_e) { /* keep polling */ }
      }, 3000);
    } catch (e) {
      setMigrating(false);
      toast.error("Drive sync failed: " + (e?.response?.data?.detail || e.message), { duration: 12000 });
    }
  };
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={syncToDrive}
        disabled={migrating}
        data-testid="drive-sync-btn"
        title="Upload all existing recordings/files that aren't yet in Google Drive (recovers files captured while Drive was disconnected)."
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono border bg-indigo-50 border-indigo-300 text-indigo-800 hover:bg-indigo-100 disabled:opacity-60"
      >
        <HardDrive size={12} />
        <span className="uppercase tracking-wider" data-testid="drive-sync-label">
          {migrating
            ? `Syncing ${migProgress?.processed || 0}/${migProgress?.total || 0}`
            : "Sync to Drive"}
        </span>
      </button>
      <button
        onClick={testUpload}
        data-testid="drive-test-btn"
        title="Try a 1-line test upload — verifies the service account can actually write to your Drive folder."
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono border bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
      >
        <HardDrive size={12} />
        <span className="uppercase tracking-wider">
          Drive: {total ? `${used}/${total} GB` : "Connected"}
        </span>
      </button>
      <button
        onClick={disconnect}
        title={`Connected as ${d.email || "?"} (${d.mode || "oauth"}). Folder: ${d.root_folder_id || "(root)"}. Click × to disconnect.`}
        className="text-slate-400 hover:text-red-600 text-[11px] px-1"
        data-testid="drive-disconnect-btn"
      >
        ×
      </button>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const admin = JSON.parse(localStorage.getItem("himt_admin") || "{}");
  const [tests, setTests] = useState([]);
  const [activeTestId, setActiveTestId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [invites, setInvites] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [ranksheet, setRanksheet] = useState(null);
  const [banks, setBanks] = useState([]);
  const [tab, setTab] = useState("tests");
  // Loading flag — prevents the "No tests yet" empty state from flashing
  // during a slow initial /admin/tests fetch (reported in a real admin
  // screenshot where a transient network lag showed an all-zeros portal).
  const [testsLoading, setTestsLoading] = useState(true);

  const api = adminApi();

  const loadTests = async () => {
    try {
      setTestsLoading(true);
      const { data } = await api.get("/admin/tests");
      setTests(data);
      // Reconcile activeTestId against the server's current list. If the
      // selected test was deleted from another session (or never saved), the
      // stale id would 404 every PATCH/GET, producing the infamous "Test not
      // found" toast when the user simply tried to add a batch. Always fall
      // back to the first real test so the UI is never wedged.
      setActiveTestId((prev) => {
        if (prev && data.some((t) => t.id === prev)) return prev;
        return data.length > 0 ? data[0].id : null;
      });
    } catch (e) {
      toast.error("Failed to load tests");
    } finally {
      setTestsLoading(false);
    }
  };

  const loadBanks = async () => {
    try {
      const { data } = await adminApi().get("/admin/question-banks");
      setBanks(data);
    } catch (e) {}
  };

  useEffect(() => { loadTests(); loadBanks(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!activeTestId) return;
    (async () => {
      try {
        // Always refresh the attempts list for the active test on every
        // test change — the stat card's "X graded · Y to grade" breakdown
        // must stay accurate regardless of which tab the admin is on.
        // (Previously this was lazy and only fired when the Attempts tab
        //  was selected, so the stat card went stale on the Rank Sheet.)
        const attemptsUrl = activeTestId === "all"
          ? "/admin/attempts"
          : `/admin/attempts?test_id=${activeTestId}`;
        api.get(attemptsUrl)
          .then(({ data }) => setAttempts(data))
          .catch(() => { /* don't toast — stat card will just stay last-known */ });

        // Lazy per-tab fetching — only pull the data the current tab
        // actually needs. Prior behaviour fired /invites + /attempts +
        // /ranksheet in parallel on every test change, which made the
        // whole portal feel heavy on slow connections.
        if (activeTestId === "all") {
          setInvites([]); setRanksheet(null); setQuestions([]);
          return;
        }
        const t = tests.find((x) => x.id === activeTestId);
        setQuestions(new Array(t?.question_count_actual || 0));
        if (tab === "invites") {
          const inv = await api.get(`/admin/tests/${activeTestId}/invites`);
          setInvites(inv.data);
        } else if (tab === "rank") {
          const rs = await api.get(`/admin/tests/${activeTestId}/ranksheet`);
          setRanksheet(rs.data);
        }
      } catch (e) {
        // If the selected test vanished (deleted elsewhere / stale id), drop
        // the selection silently and re-sync the list. Prevents a cascade of
        // red toasts on every tab switch.
        if (e?.response?.status === 404) {
          setActiveTestId(null);
          loadTests();
          return;
        }
        toast.error("Failed to refresh test data");
      }
    })();
    // eslint-disable-next-line
  }, [activeTestId, tab]);

  const downloadInstructions = async () => {
    try {
      const res = await adminApi().get("/admin/docs/student-instructions", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "HIMT-Student-Instructions.html";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded. Open in browser → Print → Save as PDF.");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadAdminManual = async () => {
    try {
      const res = await adminApi().get("/admin/docs/admin-manual", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "HIMT-Admin-Manual.html";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Admin manual downloaded. Open in browser → Print → Save as PDF.");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <Logo />
            <Overline>Evaluator Console</Overline>
          </div>
          <div className="flex items-center gap-4">
            <DrivePill />
            <StorageHealthPill />
            <div className="text-xs font-mono text-slate-600" data-testid="admin-email-display">{admin.email}</div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadInstructions}
              className="rounded-sm"
              data-testid="download-instructions-btn"
            >
              <Download size={14} className="mr-1" /> Student Guide
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadAdminManual}
              className="rounded-sm"
              data-testid="download-admin-manual-btn"
            >
              <Download size={14} className="mr-1" /> Admin Manual
            </Button>
            <Button variant="default" size="sm" onClick={() => nav("/admin")} className="rounded-sm bg-blue-600 hover:bg-blue-700" data-testid="home-btn">
              <Home size={14} className="mr-1" /> Home
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-black tracking-tighter text-[#0A2540] text-4xl">Control Room</h1>
            <p className="text-slate-600 mt-1 text-sm">Manage tests, invites, attempts, and rank sheets.</p>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Active Test</Label>
            <Select value={activeTestId || ""} onValueChange={setActiveTestId}>
              <SelectTrigger className="w-[320px] rounded-sm" data-testid="active-test-select">
                <SelectValue placeholder="Select a test" />
              </SelectTrigger>
              <SelectContent>
                {/* "All tests" mode — shows every attempt across every test
                    so admins can triage old-test records without switching
                    the active test back and forth. Invites + Rank Sheet
                    stay per-test (they're inherently scoped to one). */}
                <SelectItem value="all" data-testid="active-test-option-all">
                  <span className="font-bold">All tests</span>
                  <span className="text-xs text-slate-500 font-mono ml-2">· aggregate view</span>
                </SelectItem>
                <div className="h-px bg-slate-200 my-1" />
                {tests.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <TestDialog onSaved={loadTests} banks={banks} />
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Tests" value={tests.length} sub="Configured" />
          <StatCard label="Questions" value={banks.reduce((n, b) => n + (b.question_count || 0), 0)} sub={`across ${banks.length} bank${banks.length === 1 ? "" : "s"}`} accent="text-[#2563EB]" />
          <StatCard label="Invites" value={invites.length} sub={`${invites.filter((i) => i.is_used).length} used`} />
          {(() => {
            const graded = attempts.filter((a) => a.status === "graded").length;
            const dq = attempts.filter((a) => a.disqualified).length;
            const remaining = Math.max(0, attempts.length - graded - dq);
            return (
              <StatCard
                label="Attempts"
                value={attempts.length}
                sub={
                  <span data-testid="attempts-grading-breakdown" className="block leading-snug">
                    <span className="text-emerald-700 font-bold">{graded} graded</span>
                    <span className="mx-1.5 text-slate-300">·</span>
                    <span className={remaining > 0 ? "text-amber-700 font-bold" : "text-slate-500"}>
                      {remaining} to grade
                    </span>
                    {dq > 0 && (
                      <>
                        <span className="mx-1.5 text-slate-300">·</span>
                        <span className="text-red-700 font-bold" data-testid="attempts-dq-count">{dq} DQ</span>
                      </>
                    )}
                  </span>
                }
                accent="text-[#10B981]"
              />
            );
          })()}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-slate-200 rounded-sm p-1">
            <TabsTrigger value="tests" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-tests">
              <ClipboardList size={14} className="mr-1.5" /> Tests
            </TabsTrigger>
            <TabsTrigger value="banks" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-banks">
              <Library size={14} className="mr-1.5" /> Question Banks
            </TabsTrigger>
            <TabsTrigger value="invites" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-invites">
              <Mail size={14} className="mr-1.5" /> Invites
            </TabsTrigger>
            <TabsTrigger value="attempts" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-attempts">
              <UsersRound size={14} className="mr-1.5" /> Attempts
            </TabsTrigger>
            <TabsTrigger value="live" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-live">
              <Activity size={14} className="mr-1.5" /> Live Monitor
            </TabsTrigger>
            <TabsTrigger value="rank" className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-rank">
              <Trophy size={14} className="mr-1.5" /> Rank Sheet
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tests">
            <TestsTable tests={tests} loading={testsLoading} onChange={loadTests} activeId={activeTestId} onSelect={setActiveTestId} banks={banks} />
          </TabsContent>
          <TabsContent value="banks">
            <QuestionBanksPanel banks={banks} onChange={loadBanks} />
          </TabsContent>
          <TabsContent value="invites">
            {activeTestId === "all" ? (
              <EmptyState
                title="Invites are managed per test"
                desc="Pick a single test from the Active Test dropdown to create or manage its invites. Attempts from all tests are available in the Attempts tab."
              />
            ) : (
              <InvitesPanel
                testId={activeTestId}
                testTitle={(tests.find((t) => t.id === activeTestId) || {}).title || ""}
                invites={invites}
                onChange={async () => {
                  const { data } = await adminApi().get(`/admin/tests/${activeTestId}/invites`);
                  setInvites(data);
                }}
                onTestMissing={() => { setActiveTestId(null); loadTests(); }}
              />
            )}
          </TabsContent>
          <TabsContent value="attempts">
            <AttemptsPanel
              attempts={attempts}
              tests={tests}
              allMode={activeTestId === "all"}
              onOpen={(a) => nav(`/admin/interview/grade/${a.id}`)}
              onRefresh={async () => {
                if (!activeTestId) return;
                // Refresh BOTH attempts and ranksheet — deleting attempts must
                // immediately drop those candidates from the rank sheet too.
                if (activeTestId === "all") {
                  const att = await adminApi().get("/admin/attempts");
                  setAttempts(att.data);
                  return;
                }
                const [att, rs] = await Promise.all([
                  adminApi().get(`/admin/attempts?test_id=${activeTestId}`),
                  adminApi().get(`/admin/tests/${activeTestId}/ranksheet`),
                ]);
                setAttempts(att.data);
                setRanksheet(rs.data);
              }}
            />
          </TabsContent>
          <TabsContent value="live">
            {activeTestId === "all" ? (
              <EmptyState
                title="Live Monitor is per test"
                desc="Pick a single test from the Active Test dropdown to watch its live sessions."
              />
            ) : (
              <LiveMonitorPanel testId={activeTestId} />
            )}
          </TabsContent>
          <TabsContent value="rank">
            {activeTestId === "all" ? (
              <EmptyState
                title="Rank Sheet is per test"
                desc="Pick a single test from the Active Test dropdown to view its rank sheet + download Excel/PDF exports."
              />
            ) : (
              <RanksheetPanel
                testId={activeTestId}
                ranksheet={ranksheet}
                activeTest={tests.find((x) => x.id === activeTestId)}
                onReload={async () => {
                  if (!activeTestId) return;
                  // Refresh BOTH ranksheet AND attempts — disqualify /
                  // reinstate flips a flag the stat card depends on.
                  const [rs, att] = await Promise.all([
                    adminApi().get(`/admin/tests/${activeTestId}/ranksheet`),
                    adminApi().get(`/admin/attempts?test_id=${activeTestId}`),
                  ]);
                  setRanksheet(rs.data);
                  setAttempts(att.data);
                }}
              />
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function TestDialog({ test, onSaved, banks = [] }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(test || emptyTest);
  const [mcqBanks, setMcqBanks] = useState([]);
  const isEdit = !!test;

  // Fetch MCQ banks (from the main MCQ module) once the dialog opens so the
  // combined-exam creator can pick which banks the MCQ paper draws from.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await adminApi().get("/admin/mcq-banks");
        setMcqBanks(Array.isArray(data) ? data : []);
      } catch (e) { setMcqBanks([]); }
    })();
  }, [open]);

  useEffect(() => {
    if (test) {
      setForm({
        ...emptyTest,
        ...test,
        scheduled_start: toDatetimeLocal(test.scheduled_start),
        scheduled_end: toDatetimeLocal(test.scheduled_end),
        proctoring_config: { ...emptyTest.proctoring_config, ...(test.proctoring_config || {}) },
      });
    } else {
      setForm(emptyTest);
    }
  }, [test]);

  const save = async () => {
    try {
      const api = adminApi();
      const payload = {
        ...form,
        scheduled_start: fromDatetimeLocal(form.scheduled_start),
        scheduled_end: fromDatetimeLocal(form.scheduled_end),
      };
      if (isEdit) await api.patch(`/admin/tests/${test.id}`, payload);
      else await api.post("/admin/tests", payload);
      toast.success(isEdit ? "Test updated" : "Test created");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const togglePC = (k) => setForm({
    ...form,
    proctoring_config: { ...form.proctoring_config, [k]: !form.proctoring_config[k] },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white rounded-sm" data-testid={isEdit ? "edit-test-btn" : "new-test-btn"}>
          {isEdit ? <Pencil size={14} className="mr-1" /> : <Plus size={14} className="mr-1" />} {isEdit ? "Edit" : "New Test"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{isEdit ? "Edit Test" : "New Test Configuration"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="test-title-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" />
          </div>

          {/* Exam mode: Oral-only vs Combined (MCQ + Oral) */}
          <div className="border-2 border-[#2563EB]/30 rounded-sm p-4 bg-[#2563EB]/5">
            <Overline>Exam Type</Overline>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {[
                { v: "oral", label: "Oral only", sub: "Spoken-answer interview" },
                { v: "combined", label: "Combined (MCQ + Oral)", sub: "Single link · both phases" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setForm({ ...form, exam_mode: opt.v })}
                  className={`text-left p-3 rounded-sm border-2 transition-colors ${
                    form.exam_mode === opt.v
                      ? "border-[#2563EB] bg-white"
                      : "border-slate-200 bg-white/60 hover:border-slate-300"
                  }`}
                  data-testid={`exam-mode-${opt.v}`}
                >
                  <div className="font-bold text-sm text-[#0A2540]">{opt.label}</div>
                  <div className="text-[11px] font-mono text-slate-500 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>

            {form.exam_mode === "combined" && (
              <div className="mt-4 space-y-4" data-testid="combined-config">
                {/* Phase order */}
                <div>
                  <Label>Phase order</Label>
                  <Select value={form.phase_order} onValueChange={(v) => setForm({ ...form, phase_order: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="phase-order-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mcq_first">MCQ first, then Oral</SelectItem>
                      <SelectItem value="oral_first">Oral first, then MCQ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* MCQ source banks */}
                <div>
                  <Label>MCQ question banks (source)</Label>
                  <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100">
                    {mcqBanks.length === 0 && (
                      <div className="p-3 text-xs font-mono text-slate-400">No MCQ banks found. Create them in Admin → Question Banks.</div>
                    )}
                    {mcqBanks.map((b) => {
                      const checked = (form.mcq_bank_ids || []).includes(b.id);
                      return (
                        <label key={b.id} className="flex items-center gap-2 p-2 cursor-pointer hover:bg-slate-50 select-none">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => {
                              const set = new Set(form.mcq_bank_ids || []);
                              checked ? set.delete(b.id) : set.add(b.id);
                              setForm({ ...form, mcq_bank_ids: Array.from(set) });
                            }}
                            data-testid={`mcq-bank-${b.id}`}
                          />
                          <span className="text-sm flex-1">{b.name}</span>
                          <span className="text-[11px] font-mono text-slate-400">{b.question_count} Q</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>MCQ count</Label>
                    <Input type="number" min={1} value={form.mcq_question_count} onChange={(e) => setForm({ ...form, mcq_question_count: Number(e.target.value) })} className="rounded-sm font-mono" data-testid="mcq-count-input" />
                  </div>
                  <div>
                    <Label>MCQ time (min)</Label>
                    <Input type="number" min={1} value={form.mcq_duration_minutes} onChange={(e) => setForm({ ...form, mcq_duration_minutes: Number(e.target.value) })} className="rounded-sm font-mono" data-testid="mcq-duration-input" />
                  </div>
                  <div>
                    <Label>Negative mark</Label>
                    <Input type="number" min={0} step="0.25" value={form.mcq_negative_marking} onChange={(e) => setForm({ ...form, mcq_negative_marking: Number(e.target.value) })} className="rounded-sm font-mono" data-testid="mcq-neg-input" />
                  </div>
                </div>

                {/* Weights for combined score */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>MCQ weight (%)</Label>
                    <Input type="number" min={0} max={100} value={form.mcq_weight}
                      onChange={(e) => { const w = Number(e.target.value); setForm({ ...form, mcq_weight: w, oral_weight: 100 - w }); }}
                      className="rounded-sm font-mono" data-testid="mcq-weight-input" />
                  </div>
                  <div>
                    <Label>Oral weight (%)</Label>
                    <Input type="number" min={0} max={100} value={form.oral_weight}
                      onChange={(e) => { const w = Number(e.target.value); setForm({ ...form, oral_weight: w, mcq_weight: 100 - w }); }}
                      className="rounded-sm font-mono" data-testid="oral-weight-input" />
                  </div>
                </div>
                <p className="text-[11px] font-mono text-slate-500">Combined Score = MCQ% × {form.mcq_weight}/100 + Oral% × {form.oral_weight}/100. The Oral settings below configure the spoken phase.</p>
              </div>
            )}
          </div>

          <div>
            <Label>Assigned batches (comma-separated, leave empty for all)</Label>
            <Input
              value={Array.isArray(form.batches) ? form.batches.join(", ") : (form.batches || "")}
              onChange={(e) => setForm({ ...form, batches: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
              className="rounded-sm font-mono"
              placeholder="DNS-2026-A, DNS-2026-B"
              data-testid="test-batches-input"
            />
            <p className="text-xs text-slate-500 mt-1">Only invites with these batches can use this test.</p>
          </div>

          {/* Schedule */}
          <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
            <Overline>Schedule Window</Overline>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label>Opens at (IST)</Label>
                <Input
                  type="datetime-local"
                  value={form.scheduled_start || ""}
                  onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })}
                  className="rounded-sm font-mono"
                  data-testid="test-schedule-start-input"
                />
              </div>
              <div>
                <Label>Closes at (IST)</Label>
                <Input
                  type="datetime-local"
                  value={form.scheduled_end || ""}
                  onChange={(e) => setForm({ ...form, scheduled_end: e.target.value })}
                  className="rounded-sm font-mono"
                  data-testid="test-schedule-end-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">All times in Indian Standard Time (IST, UTC+5:30). Leave empty for open-ended access.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Questions to show</Label>
              <Input type="number" min={1} value={form.question_count} onChange={(e) => setForm({ ...form, question_count: Number(e.target.value) })} className="rounded-sm font-mono" data-testid="test-qcount-input" />
              <p className="text-[10px] text-slate-500 mt-1 font-mono">Auto-set from bank selections below.</p>
            </div>
            <div>
              <Label>TTR — seconds to start speaking</Label>
              <Input type="number" min={3} max={30} value={form.ttr_seconds} onChange={(e) => setForm({ ...form, ttr_seconds: Number(e.target.value) })} className="rounded-sm font-mono" data-testid="test-ttr-input" />
            </div>
            <div>
              <Label>Max answer duration (s)</Label>
              <Input type="number" min={10} value={form.answer_duration_seconds} onChange={(e) => setForm({ ...form, answer_duration_seconds: Number(e.target.value) })} className="rounded-sm font-mono" />
            </div>
            <div>
              <Label>Total duration (min)</Label>
              <Input type="number" min={1} value={form.total_duration_minutes} onChange={(e) => setForm({ ...form, total_duration_minutes: Number(e.target.value) })} className="rounded-sm font-mono" />
            </div>
          </div>

          {/* Question Bank Selection */}
          <BankSelectionEditor
            banks={banks}
            selections={form.question_selections || []}
            specificIds={form.specific_question_ids || []}
            onChange={(sels, specs) => {
              const total = sels.reduce((n, s) => n + (Number(s.count) || 0), 0) + (specs?.length || 0);
              setForm({
                ...form,
                question_selections: sels,
                specific_question_ids: specs,
                question_count: total || form.question_count,
              });
            }}
          />

          {/* Proctoring toggles */}
          <div className="border border-slate-200 rounded-sm p-4">
            <Overline>Proctoring Elements</Overline>
            <p className="text-xs text-slate-500 mt-1">Toggle which proctoring features to enable for this test.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
              {Object.keys(PROCTORING_LABELS).map((k) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer select-none p-2 rounded-sm hover:bg-slate-50">
                  <Checkbox
                    checked={!!form.proctoring_config?.[k]}
                    onCheckedChange={() => togglePC(k)}
                    data-testid={`proctoring-${k}-checkbox`}
                  />
                  <span className="text-sm">{PROCTORING_LABELS[k]}</span>
                </label>
              ))}
            </div>
            {!!form.proctoring_config?.screen_recording && (
              <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-medium">AI screen scan frequency</span>
                  <p className="text-xs text-slate-500 mt-0.5">How often the shared screen is captured & analysed for open apps / screen-sharing tools.</p>
                </div>
                <select
                  value={form.screen_scan_interval_minutes ?? 2}
                  onChange={(e) => setForm({ ...form, screen_scan_interval_minutes: Number(e.target.value) })}
                  className="rounded-sm border border-slate-300 text-sm font-mono px-2 py-1.5 bg-white"
                  data-testid="screen-scan-interval-select"
                >
                  <option value={0}>Off</option>
                  <option value={1}>Every 1 min</option>
                  <option value={2}>Every 2 min</option>
                  <option value={5}>Every 5 min</option>
                  <option value={10}>Every 10 min</option>
                </select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} className="bg-[#0A2540] text-white rounded-sm" data-testid="test-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestsTable({ tests, loading = false, onChange, activeId, onSelect, banks = [] }) {
  const api = adminApi();
  const remove = async (id) => {
    if (!window.confirm("Delete this test and its questions?")) return;
    await api.delete(`/admin/tests/${id}`);
    toast.success("Deleted");
    onChange();
  };
  if (loading && !tests.length) {
    return (
      <div className="mt-6 bg-white border border-slate-200 rounded-sm p-10 text-center" data-testid="tests-loading">
        <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#0A2540]" />
        <div className="mt-3 text-xs font-mono text-slate-500 tracking-widest uppercase">Loading tests…</div>
      </div>
    );
  }
  if (!tests.length) return <EmptyState title="No tests yet" desc="Create your first test to get started." />;
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="tests-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead className="font-mono">Q's</TableHead>
            <TableHead className="font-mono">TTR</TableHead>
            <TableHead className="font-mono">Duration</TableHead>
            <TableHead className="font-mono">Attempts</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tests.map((t) => (
            <TableRow key={t.id} className={activeId === t.id ? "bg-slate-50" : ""}>
              <TableCell>
                <div className="font-bold text-[#0A2540]">{t.title}</div>
                <div className="text-xs text-slate-500">{t.description || "—"}</div>
                {t.batches?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {t.batches.map((b) => (
                      <Badge key={b} variant="outline" className="rounded-sm font-mono text-[10px]">{b}</Badge>
                    ))}
                  </div>
                )}
                {(t.scheduled_start || t.scheduled_end) && (
                  <div className="text-[10px] tracking-wide font-mono text-slate-500 mt-1.5">
                    {t.scheduled_start ? fmtIST(t.scheduled_start) : "any time"}
                    <span className="mx-1">→</span>
                    {t.scheduled_end ? fmtIST(t.scheduled_end) : "open-ended"}
                  </div>
                )}
              </TableCell>
              <TableCell className="font-mono">{t.question_count_actual}/{t.question_count}</TableCell>
              <TableCell className="font-mono">{t.ttr_seconds}s</TableCell>
              <TableCell className="font-mono">{t.total_duration_minutes}m</TableCell>
              <TableCell className="font-mono">{t.attempts_count}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center gap-2 justify-end">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => onSelect(t.id)} data-testid={`select-test-${t.id}`}>Open</Button>
                  <TestDialog test={t} onSaved={onChange} banks={banks} />
                  <Button size="sm" variant="outline" className="rounded-sm text-red-600 border-red-200" onClick={() => remove(t.id)} data-testid={`delete-test-${t.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function QuestionsPanel({ bankId, onChange }) {
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyQuestion);
  const [editing, setEditing] = useState(null);

  const api = adminApi();
  const load = async () => {
    if (!bankId) return;
    const { data } = await api.get(`/admin/question-banks/${bankId}/questions`);
    setQuestions(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bankId]);

  const save = async () => {
    const payload = { ...form, key_concepts: typeof form.key_concepts === "string" ? form.key_concepts.split(",").map(s => s.trim()).filter(Boolean) : form.key_concepts };
    if (editing) await api.patch(`/admin/questions/${editing.id}`, payload);
    else await api.post(`/admin/question-banks/${bankId}/questions`, payload);
    toast.success("Saved");
    setOpen(false); setEditing(null); setForm(emptyQuestion);
    await load(); onChange && onChange();
  };
  const del = async (qid) => {
    if (!window.confirm("Delete question?")) return;
    await api.delete(`/admin/questions/${qid}`);
    toast.success("Deleted"); await load(); onChange && onChange();
  };

  const downloadSample = async () => {
    try {
      const res = await adminApi().get("/admin/questions/sample-csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "himt_questions_sample.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const bulkUpload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await adminApi().post(`/admin/question-banks/${bankId}/questions/bulk`, fd);
      toast.success(`${data.created} questions imported${data.errors?.length ? `, ${data.errors.length} errors` : ""}.`);
      await load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk upload failed"); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="bank-questions-panel">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" onClick={downloadSample} className="rounded-sm" data-testid="download-question-sample-btn">
          <Download size={14} className="mr-1" /> Sample CSV
        </Button>
        <label className="cursor-pointer">
          <input
            type="file" accept=".csv" className="hidden"
            onChange={(e) => e.target.files[0] && bulkUpload(e.target.files[0])}
            data-testid="bulk-question-upload-input"
          />
          <span className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-slate-300 rounded-sm text-sm font-medium hover:bg-slate-50">
            <Upload size={14} /> Bulk Import CSV
          </span>
        </label>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyQuestion); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2563EB] text-white rounded-sm" data-testid="add-question-btn">
              <Plus size={14} className="mr-1" /> Add Question
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-sm">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Question" : "New Question"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Question</Label>
                <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="rounded-sm" rows={3} data-testid="question-text-input" />
              </div>
              <div>
                <Label>Key concepts (comma-separated)</Label>
                <Input
                  value={Array.isArray(form.key_concepts) ? form.key_concepts.join(", ") : form.key_concepts}
                  onChange={(e) => setForm({ ...form, key_concepts: e.target.value })}
                  className="rounded-sm font-mono"
                  data-testid="question-concepts-input"
                />
              </div>
              <div>
                <Label>Ideal answer</Label>
                <Textarea value={form.ideal_answer} onChange={(e) => setForm({ ...form, ideal_answer: e.target.value })} className="rounded-sm" rows={3} />
              </div>
              <div>
                <Label>Max score</Label>
                <Input type="number" min={1} value={form.max_score} onChange={(e) => setForm({ ...form, max_score: Number(e.target.value) })} className="rounded-sm font-mono w-32" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="bg-[#0A2540] text-white rounded-sm" data-testid="question-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!questions.length ? (
        <div className="border border-dashed border-slate-300 rounded-sm p-10 text-center text-sm text-slate-500">No questions in this bank yet.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {questions.map((q, i) => (
            <div key={q.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`question-row-${q.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Overline>Question {String(i + 1).padStart(2, "0")} · Max {q.max_score}</Overline>
                  <p className="font-display font-bold text-[#0A2540] mt-2 tracking-tight">{q.text}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setEditing(q); setForm(q); setOpen(true); }} data-testid={`edit-question-${q.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => del(q.id)} data-testid={`delete-question-${q.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              {q.key_concepts?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {q.key_concepts.map((kc) => (
                    <Badge key={kc} variant="outline" className="rounded-sm font-mono text-[10px]">{kc}</Badge>
                  ))}
                </div>
              )}
              {q.ideal_answer && (
                <p className="text-xs text-slate-500 mt-3 italic leading-relaxed">Ideal: {q.ideal_answer}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionBanksPanel({ banks, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [expanded, setExpanded] = useState(null);

  const api = adminApi();
  const save = async () => {
    try {
      if (!form.name.trim()) { toast.error("Name required"); return; }
      if (editing) await api.patch(`/admin/question-banks/${editing.id}`, form);
      else await api.post("/admin/question-banks", form);
      toast.success(editing ? "Bank updated" : "Bank created");
      setOpen(false); setEditing(null); setForm({ name: "", description: "" });
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this bank and all its questions?")) return;
    try {
      await api.delete(`/admin/question-banks/${id}`);
      toast.success("Bank deleted");
      if (expanded === id) setExpanded(null);
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="mt-6 space-y-5" data-testid="banks-panel">
      <div className="flex items-center justify-between">
        <div>
          <Overline>Question Banks</Overline>
          <div className="font-display font-black text-[#0A2540] text-2xl tracking-tight mt-1">
            {banks.length} Bank{banks.length === 1 ? "" : "s"} · {banks.reduce((n, b) => n + (b.question_count || 0), 0)} Questions
          </div>
          <p className="text-xs text-slate-500 mt-1">Create reusable pools. Tests sample N random questions per bank at attempt-time.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ name: "", description: "" }); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2563EB] text-white rounded-sm" data-testid="new-bank-btn">
              <Plus size={14} className="mr-1" /> New Bank
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-sm">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Bank" : "New Question Bank"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="SOLAS Basics, Navigation, COLREGS…" className="rounded-sm" data-testid="bank-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-sm" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="bg-[#0A2540] text-white rounded-sm" data-testid="bank-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!banks.length ? (
        <EmptyState title="No question banks yet" desc="Create your first bank — then reuse it across multiple tests." />
      ) : (
        <div className="space-y-3">
          {banks.map((b) => (
            <div key={b.id} className="bg-white border border-slate-200 rounded-sm" data-testid={`bank-row-${b.id}`}>
              <div className="flex items-center justify-between p-4">
                <button
                  className="text-left flex-1"
                  onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                  data-testid={`bank-expand-${b.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Library size={18} className="text-[#2563EB]" />
                    <div>
                      <div className="font-bold text-[#0A2540]">{b.name}</div>
                      <div className="text-xs text-slate-500">{b.description || "—"}</div>
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="rounded-sm font-mono">{b.question_count} Q</Badge>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setEditing(b); setForm({ name: b.name, description: b.description || "" }); setOpen(true); }} data-testid={`edit-bank-${b.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-red-600 border-red-200" onClick={() => remove(b.id)} data-testid={`delete-bank-${b.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              {expanded === b.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50/50">
                  <QuestionsPanel bankId={b.id} onChange={onChange} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BankSelectionEditor({ banks, selections, specificIds, onChange }) {
  const add = () => {
    if (!banks.length) { toast.error("Create a Question Bank first (tab: Question Banks)"); return; }
    onChange([...selections, { bank_id: banks[0].id, count: 1 }], specificIds);
  };
  const update = (i, field, val) => {
    const next = selections.map((s, idx) => (idx === i ? { ...s, [field]: field === "count" ? Math.max(0, Number(val) || 0) : val } : s));
    onChange(next, specificIds);
  };
  const removeRow = (i) => onChange(selections.filter((_, idx) => idx !== i), specificIds);
  const total = selections.reduce((n, s) => n + (Number(s.count) || 0), 0) + (specificIds?.length || 0);

  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="bank-selection-editor">
      <div className="flex items-center justify-between">
        <div>
          <Overline>Question Selection</Overline>
          <p className="text-xs text-slate-500 mt-1">Pick N random questions from one or more banks. Each student gets a different random sample at attempt-time.</p>
        </div>
        <Badge className="rounded-sm bg-[#0A2540] text-white font-mono" data-testid="selection-total-badge">
          {total} question{total === 1 ? "" : "s"} total
        </Badge>
      </div>

      <div className="mt-4 space-y-2">
        {selections.map((s, i) => {
          const bank = banks.find((b) => b.id === s.bank_id);
          const available = bank?.question_count || 0;
          const tooMany = s.count > available;
          return (
            <div key={i} className="flex items-center gap-2" data-testid={`selection-row-${i}`}>
              <Select value={s.bank_id} onValueChange={(v) => update(i, "bank_id", v)}>
                <SelectTrigger className="flex-1 rounded-sm" data-testid={`selection-bank-${i}`}>
                  <SelectValue placeholder="Choose a bank" />
                </SelectTrigger>
                <SelectContent>
                  {banks.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name} ({b.question_count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number" min={1} max={available || 999}
                value={s.count}
                onChange={(e) => update(i, "count", e.target.value)}
                className={`w-28 rounded-sm font-mono ${tooMany ? "border-red-400 text-red-600" : ""}`}
                data-testid={`selection-count-${i}`}
              />
              <Button size="sm" variant="outline" className="rounded-sm text-red-600 border-red-200" onClick={() => removeRow(i)} data-testid={`selection-remove-${i}`}>
                <X size={14} />
              </Button>
            </div>
          );
        })}
        <Button
          type="button" variant="outline" className="rounded-sm w-full border-dashed"
          onClick={add}
          data-testid="add-selection-btn"
        >
          <Plus size={14} className="mr-1" /> Add bank selection
        </Button>
      </div>

      {!banks.length && (
        <p className="text-xs text-amber-700 mt-3 font-mono">No banks available — go to the Question Banks tab to create one first.</p>
      )}
    </div>
  );
}

function InvitesPanel({ testId, testTitle, invites, onChange, onTestMissing }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [batch, setBatch] = useState("");
  const [batchFilter, setBatchFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [assignedBatches, setAssignedBatches] = useState([]);
  // Global batch registry so admins can reuse batches created on other
  // tests without retyping — reported by real admin (Apr 2026 screenshot).
  const [globalBatches, setGlobalBatches] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [showAddBatches, setShowAddBatches] = useState(false);
  const [newBatchesInput, setNewBatchesInput] = useState("");
  const [savingBatches, setSavingBatches] = useState(false);
  const [testMissing, setTestMissing] = useState(false);
  // Free-text search across name / email / batch — ships visibility into
  // 800+ invite cohorts where scrolling alone is not viable.
  const [searchQ, setSearchQ] = useState("");

  const reloadGlobalBatches = async () => {
    try {
      const { data } = await adminApi().get("/admin/batches");
      setGlobalBatches(data.batches || []);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { reloadGlobalBatches(); }, []);

  const reloadAssignedBatches = async () => {
    if (!testId) return;
    try {
      const { data } = await adminApi().get(`/admin/tests/${testId}`);
      setAssignedBatches(data.batches || []);
      setTestMissing(false);
    } catch (e) {
      if (e?.response?.status === 404) {
        // The test the dashboard selected doesn't exist on the server anymore
        // (deleted in another session, or the state was restored from a
        // stale render). Tell the parent to clear the selection so nothing
        // else breaks and inform the user without a scary red toast.
        setTestMissing(true);
        if (onTestMissing) onTestMissing();
      }
    }
  };

  useEffect(() => {
    if (!testId) return;
    reloadAssignedBatches();
    reloadGlobalBatches();
    setSelected(new Set());
  }, [testId]);

  /**
   * Inline batch creation — so admins don't have to navigate back to the
   * Tests tab + Edit + Save just to add a batch before issuing an invite.
   * Merges the new labels into the existing `batches` array on the test.
   */
  const saveBatches = async () => {
    const newLabels = newBatchesInput
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (newLabels.length === 0) {
      toast.error("Enter at least one batch name");
      return;
    }
    setSavingBatches(true);
    try {
      const merged = Array.from(new Set([...(assignedBatches || []), ...newLabels]));
      await adminApi().patch(`/admin/tests/${testId}`, { batches: merged });
      setAssignedBatches(merged);
      setNewBatchesInput("");
      setShowAddBatches(false);
      reloadGlobalBatches();
      toast.success(`Added ${newLabels.length} batch${newLabels.length === 1 ? "" : "es"} to this test`);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Stale selection — recover gracefully instead of leaving the admin
        // staring at "Test not found" with no next action.
        setTestMissing(true);
        if (onTestMissing) onTestMissing();
        toast.error("The selected test was deleted. Refreshing…");
      } else {
        toast.error(e?.response?.data?.detail || "Failed to save batches");
      }
    } finally {
      setSavingBatches(false);
    }
  };

  /**
   * One-click attach: admin picks a batch that exists on another test but
   * isn't yet on THIS test. Now shows an EXPLICIT confirmation with the
   * test title so the admin can't accidentally attach a batch belonging
   * to another test and create invites under the wrong test (the bug
   * Garish reported on Apr 24 — "SampleIII" batch from Sample test got
   * silently attached to Engineering Test, and the invite went to the
   * wrong test).
   */
  const attachAndSelectBatch = async (label) => {
    if (!label) return;
    if ((assignedBatches || []).includes(label)) {
      setBatch(label);
      return;
    }
    const ok = window.confirm(
      `Batch "${label}" is not yet attached to this test.\n\n` +
      `Clicking OK will:\n` +
      `  • Attach "${label}" to "${testTitle || "this test"}"\n` +
      `  • Create the invite under "${testTitle || "this test"}" (NOT the test where "${label}" was originally used)\n\n` +
      `The student clicking this invite's link will see "${testTitle || "this test"}".\n\n` +
      `Continue?`
    );
    if (!ok) return;
    try {
      const merged = Array.from(new Set([...(assignedBatches || []), label]));
      await adminApi().patch(`/admin/tests/${testId}`, { batches: merged });
      setAssignedBatches(merged);
      setBatch(label);
      reloadGlobalBatches();
      toast.success(`Attached "${label}" to ${testTitle || "this test"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach batch");
    }
  };

  const create = async () => {
    if (!email || !name) return toast.error("Name and email required");
    setLoading(true);
    try {
      await adminApi().post(`/admin/tests/${testId}/invite`, { email, student_name: name, batch: batch || null });
      toast.success("Invite created");
      setEmail(""); setName("");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const copy = (inv) => {
    const url = inv.short_code
      ? `${window.location.origin}/s/${inv.short_code}`
      : `${window.location.origin}/exam/${inv.jwt}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied  ·  " + url);
  };

  const batches = Array.from(new Set(invites.map((i) => i.batch).filter(Boolean))).sort();
  const filtered = invites.filter((inv) => {
    if (batchFilter !== "all" && (inv.batch || "") !== batchFilter) return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      if (!(
        (inv.student_name || "").toLowerCase().includes(q) ||
        (inv.email || "").toLowerCase().includes(q) ||
        (inv.batch || "").toLowerCase().includes(q)
      )) return false;
    }
    return true;
  });

  if (!testId) return <EmptyState title="Select a test" desc="Pick an active test above." />;

  const downloadSample = async () => {
    try {
      // Pass test_id so the sample rows use the current test's actual
      // configured batches instead of hardcoded `DNS-2026-A/B` strings.
      // Admins reported they had to rename every placeholder by hand.
      const url = testId
        ? `/admin/invites/sample-csv?test_id=${encodeURIComponent(testId)}`
        : "/admin/invites/sample-csv";
      const res = await adminApi().get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = blobUrl; a.download = "himt_students_sample.csv"; a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error("Download failed"); }
  };

  const exportInvites = async () => {
    try {
      const origin = window.location.origin;
      const res = await adminApi().get(
        `/admin/tests/${testId}/invites/export-csv?request_host=${encodeURIComponent(origin)}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "himt_invites_export.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Invites CSV downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  const bulkUpload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await adminApi().post(`/admin/tests/${testId}/invites/bulk`, fd);
      toast.success(`${data.created} invites generated${data.errors?.length ? `, ${data.errors.length} errors` : ""}.`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk upload failed"); }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-6">
      {testMissing && (
        <div className="lg:col-span-3 p-4 border border-amber-300 bg-amber-50 rounded-sm text-sm text-amber-900" data-testid="test-missing-banner">
          <b>The selected test no longer exists.</b> It may have been deleted in another session. Please pick another test from the <i>Tests</i> tab.
        </div>
      )}
      <div className="lg:col-span-1 space-y-4">
        <div className="bg-white border border-slate-200 rounded-sm p-6 h-fit">
          <Overline>Create one-time link</Overline>
          {/* Prominent active-test banner. The admin MUST see which test they
              are creating invites for — the "wrong test in invite" bug
              (Apr 24) happened when this was implicit. */}
          <div
            className="mt-3 px-3 py-2 bg-[#0A2540] text-white rounded-sm flex items-center gap-2"
            data-testid="invite-test-banner"
          >
            <span className="text-[9px] font-mono tracking-widest uppercase opacity-70">For test</span>
            <span className="font-bold text-sm truncate flex-1">{testTitle || "—"}</span>
          </div>
          <div className="space-y-3 mt-4">
            <div>
              <Label>Student name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="invite-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" data-testid="invite-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Batch {assignedBatches.length > 0 ? "(required)" : "(optional)"}</Label>
                <button
                  type="button"
                  onClick={() => setShowAddBatches((v) => !v)}
                  className="text-[10px] font-mono tracking-widest uppercase text-[#2563EB] hover:underline"
                  data-testid="toggle-add-batches-btn"
                >
                  {showAddBatches ? "Cancel" : (assignedBatches.length === 0 ? "+ Add batches" : "+ Add more")}
                </button>
              </div>

              {(() => {
                const assignedSet = new Set(assignedBatches || []);
                const otherBatches = (globalBatches || []).filter((b) => !assignedSet.has(b));
                // Show the select whenever ANY batch exists anywhere (this
                // test OR other tests). Admin screenshot case: GME30 only
                // on current test + other batches created on other tests —
                // previously invisible here, now reachable via the
                // "Other batches" group.
                const hasAnyBatch = assignedBatches.length > 0 || otherBatches.length > 0;
                if (!hasAnyBatch) {
                  return (
                    <Input
                      value={batch}
                      onChange={(e) => setBatch(e.target.value)}
                      className="rounded-sm font-mono"
                      placeholder="DNS-2026-A"
                      data-testid="invite-batch-input"
                    />
                  );
                }
                return (
                  <Select
                    value={batch}
                    onValueChange={(val) => {
                      // If the picked batch isn't yet attached to this
                      // test, auto-attach it before selecting. One click.
                      if (assignedSet.has(val)) setBatch(val);
                      else attachAndSelectBatch(val);
                    }}
                  >
                    <SelectTrigger className="rounded-sm font-mono" data-testid="invite-batch-select">
                      <SelectValue placeholder="Pick a batch" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignedBatches.length > 0 && (
                        <>
                          <div className="px-2 pt-1.5 pb-1 text-[9px] font-mono font-bold tracking-widest uppercase text-slate-500">
                            Assigned to this test
                          </div>
                          {assignedBatches.map((b) => (
                            <SelectItem key={`a-${b}`} value={b} data-testid={`batch-option-${b}`}>{b}</SelectItem>
                          ))}
                        </>
                      )}
                      {otherBatches.length > 0 && (
                        <>
                          <div className="px-2 pt-2 pb-1 text-[9px] font-mono font-bold tracking-widest uppercase text-slate-500 border-t border-slate-200 mt-1">
                            Other batches (auto-attach)
                          </div>
                          {otherBatches.map((b) => (
                            <SelectItem
                              key={`o-${b}`}
                              value={b}
                              data-testid={`batch-option-other-${b}`}
                            >
                              {b}
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                );
              })()}

              {assignedBatches.length > 0 ? (
                <p className="text-[10px] text-slate-500 mt-1.5 font-mono uppercase tracking-wider">
                  Assigned to this test · {assignedBatches.length} {assignedBatches.length === 1 ? "batch" : "batches"}
                </p>
              ) : (
                <p className="text-[10px] text-slate-500 mt-1.5 font-mono uppercase tracking-wider">
                  No batches assigned — any label accepted. Tap <b>+ Add batches</b> to restrict.
                </p>
              )}

              {showAddBatches && (
                <div className="mt-3 p-3 border border-slate-300 rounded-sm bg-slate-50" data-testid="add-batches-panel">
                  <Label className="text-[11px]">New batch name(s) — comma or newline separated</Label>
                  <Input
                    value={newBatchesInput}
                    onChange={(e) => setNewBatchesInput(e.target.value)}
                    placeholder="DNS-2026-A, DNS-2026-B"
                    className="rounded-sm font-mono mt-1"
                    data-testid="new-batches-input"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveBatches(); } }}
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      onClick={saveBatches}
                      disabled={savingBatches || !newBatchesInput.trim()}
                      className="bg-[#0A2540] text-white rounded-sm text-xs px-4 py-2 h-8 disabled:opacity-50"
                      data-testid="save-new-batches-btn"
                    >
                      {savingBatches ? "Saving…" : "Save to test"}
                    </Button>
                    <span className="text-[10px] text-slate-500 font-mono self-center">
                      Merges into the test's assigned batches.
                    </span>
                  </div>
                </div>
              )}
            </div>
            <Button onClick={create} disabled={loading} className="w-full bg-[#0A2540] text-white rounded-sm" data-testid="invite-create-btn">
              <LinkIcon size={14} className="mr-1" /> {loading ? "Generating..." : "Generate Link"}
            </Button>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
          <Overline>Bulk import</Overline>
          <p className="text-xs text-slate-600 mt-2 leading-relaxed">
            Upload a CSV of students (student_name, email, batch). One-time test links will be generated for each.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="outline" onClick={downloadSample} className="rounded-sm" data-testid="download-invite-sample-btn">
              <Download size={14} className="mr-1" /> Sample CSV
            </Button>
            <label className="cursor-pointer">
              <input
                type="file" accept=".csv" className="hidden"
                onChange={(e) => e.target.files[0] && bulkUpload(e.target.files[0])}
                data-testid="bulk-invite-upload-input"
              />
              <span className="inline-flex items-center gap-1 px-3 py-2 bg-[#2563EB] text-white rounded-sm text-sm font-bold">
                <Upload size={14} /> Upload CSV
              </span>
            </label>
          </div>
        </div>

        {invites.length > 0 && (
          <Button variant="outline" onClick={exportInvites} className="w-full rounded-sm" data-testid="export-invites-btn">
            <Download size={14} className="mr-1" /> Export Invites + Links
          </Button>
        )}
      </div>

      <div className="lg:col-span-2">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Input
            placeholder="Search name, email, batch…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="w-64 rounded-sm font-mono"
            data-testid="invites-search-input"
          />
          {batches.length > 0 && (
            <>
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Batch</Label>
              <Select value={batchFilter} onValueChange={setBatchFilter}>
                <SelectTrigger className="w-[220px] rounded-sm" data-testid="batch-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All batches</SelectItem>
                  {batches.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <span className="ml-auto text-xs text-slate-500 font-mono">
            <b className="text-[#0A2540]">{filtered.length}</b> of {invites.length}
          </span>
        </div>
        {!filtered.length ? (
          <EmptyState title={searchQ || batchFilter !== "all" ? "No matching invites" : "No invites yet"} desc={searchQ || batchFilter !== "all" ? "Try clearing the search or batch filter." : "Generated invite links will appear here."} />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            {selected.size > 0 && (
              <div className="flex items-center justify-between px-4 py-3 bg-[#0A2540] text-white">
                <div className="text-sm font-bold">{selected.size} selected</div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-white hover:bg-white/10 rounded-sm" data-testid="clear-selection-btn">Clear</Button>
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm(`Delete ${selected.size} invite(s)? This cannot be undone.`)) return;
                      try {
                        const { data } = await adminApi().post("/admin/invites/bulk-delete", { ids: Array.from(selected) });
                        toast.success(`${data.deleted} invite(s) deleted`);
                        setSelected(new Set());
                        onChange();
                      } catch (e) { toast.error("Delete failed"); }
                    }}
                    className="bg-red-600 text-white hover:bg-red-700 rounded-sm"
                    data-testid="bulk-delete-invites-btn"
                  >
                    <Trash2 size={14} className="mr-1" /> Delete selected
                  </Button>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={filtered.length > 0 && filtered.every((i) => selected.has(i.id))}
                      onCheckedChange={(v) => {
                        if (v) setSelected(new Set(filtered.map((i) => i.id)));
                        else setSelected(new Set());
                      }}
                      data-testid="select-all-invites-checkbox"
                    />
                  </TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Batch</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((inv) => (
                  <TableRow key={inv.id} className={selected.has(inv.id) ? "bg-slate-50" : ""}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(inv.id)}
                        onCheckedChange={(v) => {
                          const s = new Set(selected);
                          if (v) s.add(inv.id); else s.delete(inv.id);
                          setSelected(s);
                        }}
                        data-testid={`select-invite-${inv.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-semibold text-[#0A2540]">{inv.student_name}</TableCell>
                    <TableCell className="font-mono text-xs">{inv.email}</TableCell>
                    <TableCell className="font-mono text-xs">{inv.batch || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell>
                      {inv.is_used
                        ? <Badge className="rounded-sm bg-slate-200 text-slate-700 hover:bg-slate-200">Used</Badge>
                        : <Badge className="rounded-sm bg-green-600 text-white">Active</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="rounded-sm" onClick={() => copy(inv)} data-testid={`copy-invite-${inv.id}`}>
                          <Copy size={14} className="mr-1" /> Copy Link
                        </Button>
                        {inv.short_code && (
                          <span className="text-[11px] font-mono text-slate-400 select-all">/s/{inv.short_code}</span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm text-red-600 border-red-200"
                          onClick={async () => {
                            if (!window.confirm(`Delete invite for ${inv.student_name}?`)) return;
                            try {
                              await adminApi().delete(`/admin/invites/${inv.id}`);
                              toast.success("Invite deleted");
                              onChange();
                            } catch (e) { toast.error("Delete failed"); }
                          }}
                          data-testid={`delete-invite-${inv.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function AttemptsPanel({ attempts, onOpen, onRefresh, tests = [], allMode = false }) {
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [testFilter, setTestFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [bulkForcing, setBulkForcing] = useState(false);

  // Reset selection whenever the attempts list itself changes identity.
  useEffect(() => { setSelected(new Set()); }, [attempts]);

  // test_id → title map so we can label each row in All-Tests mode.
  const testTitle = (id) => (tests.find((t) => t.id === id)?.title) || "—";

  const now = Date.now();
  const STUCK_MINS = 90;

  const filtered = attempts.filter((a) => {
    if (testFilter !== "all" && a.test_id !== testFilter) return false;
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      if (!(
        (a.student_name || "").toLowerCase().includes(q) ||
        (a.student_email || "").toLowerCase().includes(q) ||
        (a.batch || "").toLowerCase().includes(q)
      )) return false;
    }
    return true;
  });

  const stuckAttempts = attempts.filter((a) =>
    (a.status === "in_progress" || a.status === "environment_check") &&
    (now - new Date(a.started_at).getTime()) > STUCK_MINS * 60 * 1000
  );

  const bulkForceSubmit = async () => {
    if (!stuckAttempts.length) return;
    if (!window.confirm(`Force-submit ALL ${stuckAttempts.length} stuck attempts and queue grading?`)) return;
    setBulkForcing(true);
    let done = 0;
    for (const a of stuckAttempts) {
      try {
        await adminApi().post(`/admin/attempts/${a.id}/force-submit`);
        done++;
      } catch (_) {}
    }
    toast.success(`Force-submitted ${done} of ${stuckAttempts.length} stuck attempts.`);
    setBulkForcing(false);
    onRefresh();
  };

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const allSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((a) => a.id)));

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const ok = window.confirm(
      `Delete ${ids.length} attempt(s)? This also removes all answers, recordings, and 360° scan videos for these candidates. The associated invite links will be reset so the students can retake if re-invited. This cannot be undone.`,
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const { data } = await adminApi().post("/admin/attempts/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted_attempts} attempt(s), ${data.deleted_answers} answer(s), ${data.reset_tokens} token(s) reset.`);
      setSelected(new Set());
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-6">
      {/* Filter + search toolbar — works in both All-Tests and single-test
          modes. Test filter is only visible in All mode (single mode is
          already scoped). */}
      <div className="mb-4 bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search name, email, batch…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          className="w-64 rounded-sm font-mono"
          data-testid="attempts-search-input"
        />
        {allMode && (
          <>
            <span className="text-[10px] font-mono font-bold tracking-widest uppercase text-slate-500">Test</span>
            <Select value={testFilter} onValueChange={setTestFilter}>
              <SelectTrigger className="w-64 rounded-sm font-mono" data-testid="attempts-test-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tests</SelectItem>
                {tests.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
        <span className="text-[10px] font-mono font-bold tracking-widest uppercase text-slate-500">Status</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-sm font-mono" data-testid="attempts-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="grading">Grading</SelectItem>
            <SelectItem value="graded">Graded</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs font-mono text-slate-500">
          Showing <b className="text-[#0A2540]">{filtered.length}</b> of {attempts.length}
        </div>
      </div>

      {!attempts.length && (
        <EmptyState
          title="No attempts yet"
          desc={allMode
            ? "No candidate has taken any test yet. Create an invite to begin."
            : "Attempts from invited students will appear here."}
        />
      )}

      {/* Stuck attempts banner */}
      {stuckAttempts.length > 0 && (
        <div className="mb-3 flex items-center justify-between bg-amber-50 border border-amber-300 rounded-sm px-4 py-2" data-testid="stuck-banner">
          <span className="text-sm font-mono text-amber-800">
            <b>{stuckAttempts.length}</b> attempt{stuckAttempts.length > 1 ? "s" : ""} stuck &gt;90 min (no submission)
          </span>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs"
            disabled={bulkForcing}
            onClick={bulkForceSubmit}
            data-testid="bulk-force-submit-btn"
          >
            {bulkForcing ? "Submitting…" : `Force Submit All ${stuckAttempts.length}`}
          </Button>
        </div>
      )}

      {attempts.length > 0 && (
      <>
      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-red-50 border border-red-200 rounded-sm px-4 py-2">
          <span className="text-sm text-red-800 font-mono" data-testid="bulk-selection-banner">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="rounded-sm text-slate-600" data-testid="bulk-clear-btn">
              Clear
            </Button>
            <Button
              size="sm"
              onClick={bulkDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              data-testid="bulk-delete-attempts-btn"
            >
              <Trash2 size={14} className="mr-1.5" />
              {deleting ? "Deleting..." : `Delete ${selected.size} attempt(s)`}
            </Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  data-testid="select-all-attempts"
                  aria-label="Select all attempts"
                />
              </TableHead>
              <TableHead>Student</TableHead>
              {allMode && <TableHead>Test</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="font-mono">AI</TableHead>
              <TableHead className="font-mono">Human</TableHead>
              <TableHead className="font-mono">Final</TableHead>
              <TableHead className="font-mono" title="Average AI-content % across answered questions">Avg AI %</TableHead>
              <TableHead className="font-mono" title="Off-screen gaze % from server-side eye-movement analysis. ≥60% triggers 50% score cap.">Gaze %</TableHead>
              <TableHead className="font-mono">Flags</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((a) => (
              <TableRow key={a.id} data-state={selected.has(a.id) ? "selected" : undefined}>
                <TableCell>
                  <Checkbox
                    checked={selected.has(a.id)}
                    onCheckedChange={() => toggle(a.id)}
                    data-testid={`select-attempt-${a.id}`}
                    aria-label={`Select attempt for ${a.student_name}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="font-bold text-[#0A2540]">{a.student_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.student_email}</div>
                </TableCell>
                {allMode && (
                  <TableCell>
                    <div className="text-xs font-mono text-slate-600 max-w-[180px] truncate" title={testTitle(a.test_id)}>
                      {testTitle(a.test_id)}
                    </div>
                    {a.batch && <div className="text-[10px] font-mono text-slate-400">{a.batch}</div>}
                  </TableCell>
                )}
                <TableCell>
                  {a.disqualified ? (
                    <Badge className="rounded-sm bg-red-600 text-white" data-testid={`dq-badge-attempts-${a.id}`}>DQ</Badge>
                  ) : (
                    <Badge className={`rounded-sm ${a.status === "graded" ? "bg-[#0A2540] text-white" : a.status === "submitted" ? "bg-[#2563EB] text-white" : "bg-slate-200 text-slate-700"}`}>
                      {a.status}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-mono">{a.ai_total_score ?? "—"}</TableCell>
                <TableCell className="font-mono">{a.human_total_score ?? "—"}</TableCell>
                <TableCell className="font-mono font-bold">{a.final_score ?? "—"}</TableCell>
                <TableCell className="font-mono" data-testid={`attempt-ai-pct-${a.id}`}>
                  {a.avg_ai_content_score == null ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <span className={
                      a.avg_ai_content_score >= 60 ? "text-red-600 font-bold" :
                      a.avg_ai_content_score >= 40 ? "text-amber-600 font-bold" :
                      "text-emerald-700"
                    }
                    title={`Average across ${a.ai_content_answer_count} answered question${a.ai_content_answer_count === 1 ? "" : "s"}`}
                    >
                      {a.avg_ai_content_score}%
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono" data-testid={`attempt-gaze-pct-${a.id}`}>
                  {a.gaze_off_screen_pct == null ? (
                    <span className="text-slate-400" title={a.gaze_skip_reason || "No gaze analysis yet"}>—</span>
                  ) : (
                    <span className={
                      a.gaze_off_screen_pct >= 60 ? "text-red-600 font-bold" :
                      a.gaze_off_screen_pct >= 30 ? "text-amber-600 font-bold" :
                      "text-emerald-700"
                    }
                    title={a.gaze_indicators || "Off-screen gaze %"}
                    >
                      {a.gaze_off_screen_pct}%
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono">
                  <span className={(a.proctoring_flags?.length || 0) > 3 ? "text-red-600 font-bold" : ""}>
                    {a.proctoring_flags?.length || 0}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{a.submitted_at ? fmtIST(a.submitted_at) : "—"}</TableCell>
                <TableCell className="text-right">
                  {/* GPS location */}
                  {a.gps_location?.latitude ? (
                    <a
                      href={`https://maps.google.com/?q=${a.gps_location.latitude},${a.gps_location.longitude}`}
                      target="_blank" rel="noreferrer"
                      title={`GPS: ${a.gps_location.latitude.toFixed(4)}, ${a.gps_location.longitude.toFixed(4)} (±${Math.round(a.gps_location.accuracy_m || 0)}m)`}
                      className="text-[11px] font-mono text-blue-600 underline mr-2"
                      data-testid={`gps-link-${a.id}`}
                    >
                      GPS
                    </a>
                  ) : (a.gps_status === "denied" || a.gps_status === "unavailable") ? (
                    <span
                      title="Candidate did not share location"
                      className="text-[11px] font-mono text-amber-600 mr-2 inline-flex items-center gap-0.5"
                      data-testid={`gps-missing-${a.id}`}
                    >
                      <MapPinOff size={11} /> No GPS
                    </span>
                  ) : null}
                  {/* Force submit for stuck attempts */}
                  {(a.status === "in_progress" || a.status === "environment_check") && (
                    <>
                      <Button
                        size="sm" variant="outline"
                        className="rounded-sm text-xs border-amber-400 text-amber-700 hover:bg-amber-50 mr-1"
                        data-testid={`force-submit-${a.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Force-submit ${a.student_name}'s stuck attempt and trigger grading?`)) return;
                          try {
                            await adminApi().post(`/admin/attempts/${a.id}/force-submit`);
                            toast.success("Force-submitted. Grading queued.");
                            onRefresh();
                          } catch (e) { toast.error("Failed: " + (e?.response?.data?.detail || e.message)); }
                        }}
                      >Force Submit</Button>
                      <Button
                        size="sm" variant="outline"
                        className="rounded-sm text-xs border-blue-400 text-blue-700 hover:bg-blue-50 mr-1"
                        data-testid={`reset-attempt-${a.id}`}
                        title="Wipe all answers and let the candidate start the test again from question 1"
                        onClick={async () => {
                          if (!window.confirm(
                            `Reset ${a.student_name}'s attempt?\n\n` +
                            `• All ${a.question_ids?.length || "?"} previously submitted answers will be DELETED.\n` +
                            `• Their invite link will be re-armed.\n` +
                            `• They'll re-open the same link and start from question 1.\n\n` +
                            `Continue?`
                          )) return;
                          try {
                            const { data } = await adminApi().post(`/admin/attempts/${a.id}/reset`);
                            toast.success(`Reset done — ${data.deleted_answers} answer(s) cleared. Share the invite link with the candidate.`);
                            onRefresh();
                          } catch (e) { toast.error("Reset failed: " + (e?.response?.data?.detail || e.message)); }
                        }}
                      >Reset &amp; Resume</Button>
                    </>
                  )}
                  <Button size="sm" className="bg-[#0A2540] text-white rounded-sm" onClick={() => onOpen(a)} data-testid={`grade-attempt-${a.id}`}>
                    <Bot size={14} className="mr-1" /> Grade
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      </>
      )}
    </div>
  );
}

function RanksheetPanel({ testId, ranksheet: fallback, activeTest, onReload }) {
  const nav = useNavigate();
  const [batch, setBatch] = useState("all");
  const [data, setData] = useState(fallback || null);
  const [loading, setLoading] = useState(false);
  // Search across candidate name / email / batch — large cohorts (>500
  // candidates) made finding a single rank intractable without it.
  const [searchQ, setSearchQ] = useState("");
  // Rank-sheet view: "detailed" (full numeric table) | "screening" (clean,
  // decision-oriented report with rich proctoring remarks).
  const [view, setView] = useState("detailed");
  // Sort by Avg AI % toggle: null = default rank order, "desc" = highest
  // AI-content first (most-suspect candidates), "asc" = lowest first.
  const [aiSort, setAiSort] = useState(null);
  // Sort by off-screen gaze % — same UX. Sorts override each other (only
  // one column drives the sort at a time).
  const [gazeSort, setGazeSort] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [gazeBackfilling, setGazeBackfilling] = useState(false);
  const [gazeProgress, setGazeProgress] = useState(null);  // running snapshot from /backfill-gaze/status
  // When true, the next backfill will also re-run analysis on attempts that
  // previously got a score. Necessary if the analysis code was buggy and
  // produced `gaze_analysis_status: "failed"` rows that should be retried.
  const [gazeOverwrite, setGazeOverwrite] = useState(false);
  // Screen-share re-analysis (mirrors gaze backfill).
  const [screenProgress, setScreenProgress] = useState(null);
  const [screenBackfilling, setScreenBackfilling] = useState(false);
  const [screenOverwrite, setScreenOverwrite] = useState(false);
  // Rank table filter: show only candidates flagged for screen-sharing.
  const [onlyScreenShare, setOnlyScreenShare] = useState(false);

  // Poll the gaze-backfill status singleton every 4s when the rank tab
  // is mounted. Light enough to keep running always — the endpoint just
  // returns a single tiny doc — and the UI auto-refreshes when a backfill
  // started elsewhere completes.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await adminApi().get("/admin/gaze-backfill/status");
        if (!cancelled) setGazeProgress(data);
        // When the job flips from running → done, also reload the ranksheet
        // so the new gaze % values appear in the table.
        if (!cancelled && data?.status === "done" && gazeProgress?.status === "running") {
          if (typeof onReload === "function") await onReload();
        }
      } catch { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line
  }, []);

  // Poll the screen-scan backfill status singleton.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await adminApi().get("/admin/screen-scan-backfill/status");
        if (!cancelled) setScreenProgress(data);
        if (!cancelled && data?.status === "done" && screenProgress?.status === "running") {
          if (typeof onReload === "function") await onReload();
        }
      } catch { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line
  }, []);

  const batches = activeTest?.batches || [];

  // Initial data comes from parent; switching batch re-fetches scoped view.
  useEffect(() => { setData(fallback); }, [fallback]);

  const refetch = async (b = batch) => {
    if (!testId) return;
    setLoading(true);
    try {
      const url = b && b !== "all"
        ? `/admin/tests/${testId}/ranksheet?batch=${encodeURIComponent(b)}`
        : `/admin/tests/${testId}/ranksheet`;
      const rs = await adminApi().get(url);
      setData(rs.data);
    } catch (e) {
      toast.error("Failed to load rank sheet");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (batch === "all") { setData(fallback); return; }
    refetch(batch);
    /* eslint-disable-next-line */
  }, [batch]);

  const downloadFile = async (format) => {
    if (!testId) return;
    const qs = new URLSearchParams();
    if (batch && batch !== "all") qs.set("batch", batch);
    const isScreening = view === "screening";
    const base = isScreening ? "screening-report/export" : "ranksheet/export";
    const url = `/admin/tests/${testId}/${base}.${format}?${qs.toString()}`;
    try {
      const res = await adminApi().get(url, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      // Prefer the server-supplied filename (includes test title + batch).
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const suffix = batch && batch !== "all" ? `_${batch}` : "";
      const stem = isScreening ? "himt_screening" : "himt_ranksheet";
      a.download = m ? m[1] : `${stem}${suffix}.${format === "xlsx" ? "xlsx" : "html"}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
      if (format === "pdf") {
        toast.success(`${isScreening ? "Screening report" : "Ranksheet"} downloaded. Open the file → your browser auto-prints → Save as PDF.`);
      } else {
        toast.success(`${isScreening ? "Screening report" : "Excel ranksheet"} downloaded.`);
      }
    } catch (e) {
      const code = e?.response?.status;
      toast.error(code ? `Download failed (HTTP ${code})` : "Download failed");
    }
  };

  const disqualify = async (row) => {
    const reason = window.prompt(
      `Disqualify ${row.student_name}?\n\nEnter a reason (will appear on the ranksheet):`,
      ""
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      await adminApi().post(`/admin/attempts/${row.attempt_id}/disqualify`, { reason: reason.trim() });
      toast.success(`${row.student_name} marked as disqualified.`);
      await refetch();
      if (onReload) onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disqualify failed");
    }
  };

  const reinstate = async (row) => {
    if (!window.confirm(`Reinstate ${row.student_name}? Their rank will be restored.`)) return;
    try {
      await adminApi().post(`/admin/attempts/${row.attempt_id}/reinstate`);
      toast.success(`${row.student_name} reinstated.`);
      await refetch();
      if (onReload) onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reinstate failed");
    }
  };

  if (!data || !data.rows?.length) {
    return (
      <div className="mt-6 space-y-4">
        <RankToolbar
          batch={batch} setBatch={setBatch} batches={batches}
          onExportXlsx={() => downloadFile("xlsx")}
          onExportPdf={() => downloadFile("pdf")}
          disabled
        />
        <EmptyState title="Rank sheet empty" desc="Grades will surface here after attempts are submitted & evaluated." />
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      <RankToolbar
        batch={batch} setBatch={setBatch} batches={batches}
        searchQ={searchQ} setSearchQ={setSearchQ}
        onExportXlsx={() => downloadFile("xlsx")}
        onExportPdf={() => downloadFile("pdf")}
      />
      <RankViewToggle view={view} setView={setView} />
      <ScoreLegend isCombined={data.is_combined} />
      {view === "screening" && <ScreeningReport testId={testId} batch={batch} />}
      {view === "detailed" && (
      <div className="space-y-4">
      {/* Retroactive AI-content penalty trigger. Pre-existing graded answers
          stay untouched until an admin runs this — the cap rule was
          introduced after some attempts had already been graded. */}
      <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="ai-penalty-backfill-bar">
        <div className="text-xs text-slate-700 leading-snug">
          <b className="text-amber-800">AI-content penalty (≥60% → 50% cap)</b> is applied automatically on new grading runs. Already-graded attempts keep their original scores until you backfill.
        </div>
        <Button
          size="sm" variant="outline"
          className="rounded-sm border-amber-400 text-amber-800 hover:bg-amber-100"
          disabled={backfilling}
          data-testid="backfill-ai-penalty-btn"
          onClick={async () => {
            if (!window.confirm(
              `Re-evaluate every already-graded answer for THIS test and apply the\n` +
              `60% → 50% AI-content cap retroactively?\n\n` +
              `• Answers with ai_content_score < 60% are untouched.\n` +
              `• Capped answers get their raw score preserved as ai_score_raw.\n` +
              `• Each affected attempt's ai_total_score / final_score is recomputed.\n` +
              `• Safe to repeat — already-capped answers are skipped.\n\n` +
              `Continue?`
            )) return;
            try {
              setBackfilling(true);
              const { data: res } = await adminApi().post(
                `/admin/attempts/backfill-ai-penalty?test_id=${testId}`
              );
              toast.success(
                `Backfill complete · ${res.answers_capped} answer(s) capped across ` +
                `${res.attempts_recomputed} attempt(s) (scanned ${res.answers_scanned}).`,
                { duration: 8000 }
              );
              if (typeof onReload === "function") await onReload();
            } catch (e) {
              toast.error("Backfill failed: " + (e?.response?.data?.detail || e.message));
            } finally {
              setBackfilling(false);
            }
          }}
        >
          {backfilling ? "Applying…" : "Apply AI penalty retroactively"}
        </Button>
      </div>

      {/* Gaze / eye-movement retroactive backfill. Heavy job (each attempt
          ~3-10s of CPU). Runs as a background task and reports progress.
          The progress banner is scoped to the active test — if a backfill
          is running for ANOTHER test, this UI stays in idle state so the
          admin doesn't think their current test is being processed. */}
      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="gaze-backfill-bar">
        <div className="text-xs text-slate-700 leading-snug">
          <b className="text-blue-800">Eye-movement analysis (off-screen gaze %)</b> runs automatically on new submissions. Already-graded attempts can be analysed retroactively.
          {gazeProgress?.status === "running" && (
            <span className="block mt-1 font-mono text-blue-700" data-testid="gaze-backfill-progress">
              Processing <b>{gazeProgress.processed}/{gazeProgress.total}</b>… (done {gazeProgress.succeeded} · skipped {gazeProgress.skipped} · failed {gazeProgress.failed} · capped {gazeProgress.capped})
              {gazeProgress.test_id !== testId && (
                <span className="ml-2 text-slate-500 italic text-[11px]">
                  (running for {gazeProgress.scope || "another test"})
                </span>
              )}
            </span>
          )}
          {gazeProgress?.status === "done" && gazeProgress.test_id === testId && (
            <span className="block mt-1 font-mono text-emerald-700">
              Last run: {gazeProgress.succeeded} done, {gazeProgress.skipped} skipped, {gazeProgress.failed} failed, {gazeProgress.capped} capped.
            </span>
          )}
          {gazeProgress?.status === "cancelled" && (
            <span className="block mt-1 font-mono text-amber-700">
              Backfill cancelled at {gazeProgress.processed}/{gazeProgress.total}. {gazeProgress.succeeded} attempts completed before cancel.
            </span>
          )}
          {gazeProgress?.status === "interrupted" && (
            <span className="block mt-1 font-mono text-red-700">
              Previous backfill interrupted (server restart) at {gazeProgress.processed || 0}/{gazeProgress.total || 0}. Click Run to resume.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {gazeProgress?.status === "running" && (
            <Button
              size="sm" variant="outline"
              className="rounded-sm border-red-400 text-red-700 hover:bg-red-50"
              data-testid="cancel-gaze-backfill-btn"
              onClick={async () => {
                if (!window.confirm(
                  `Cancel the running gaze backfill?\n\n` +
                  `Currently processing ${gazeProgress.processed}/${gazeProgress.total} for ${gazeProgress.scope || "this test"}.\n` +
                  `Already-finished attempts keep their gaze scores.\n\n` +
                  `If the job appears stuck (no progress for >5 minutes), use Force Reset instead.`
                )) return;
                try {
                  await adminApi().post("/admin/gaze-backfill/cancel");
                  toast.info("Cancel requested. Already-running attempts will finish, then the job stops.");
                } catch (e) {
                  toast.error("Cancel failed: " + (e?.response?.data?.detail || e.message));
                }
              }}
            >
              Cancel
            </Button>
          )}
          {(gazeProgress?.status === "running" || gazeProgress?.status === "interrupted") && (
            <Button
              size="sm" variant="outline"
              className="rounded-sm border-slate-400 text-slate-600 hover:bg-slate-100"
              data-testid="reset-gaze-backfill-btn"
              title="Clear the backfill status singleton. Use only if the job is genuinely stuck (no progress for many minutes after a backend restart)."
              onClick={async () => {
                if (!window.confirm(
                  `Force-reset the gaze backfill state?\n\n` +
                  `This clears the "running" flag in the database without stopping any actual workers ` +
                  `(if a real worker is still alive, it will keep updating the singleton).\n\n` +
                  `Use this ONLY when the status has been stuck without progress and you're sure the worker died.\n\n` +
                  `Continue?`
                )) return;
                try {
                  await adminApi().post("/admin/gaze-backfill/reset");
                  toast.success("Backfill state reset. You can start a fresh run now.");
                } catch (e) {
                  toast.error("Reset failed: " + (e?.response?.data?.detail || e.message));
                }
              }}
            >
              Force reset
            </Button>
          )}
          <label className="flex items-center gap-1.5 text-[11px] font-mono text-slate-600 cursor-pointer select-none" title="Also re-analyse attempts that already have a score. Use this after fixing analysis bugs.">
            <input
              type="checkbox"
              checked={gazeOverwrite}
              onChange={(e) => setGazeOverwrite(e.target.checked)}
              className="accent-blue-600"
              data-testid="gaze-overwrite-toggle"
            />
            Re-analyse all (overwrite)
          </label>
          <Button
          size="sm" variant="outline"
          className="rounded-sm border-blue-400 text-blue-800 hover:bg-blue-100"
          disabled={gazeBackfilling || (gazeProgress?.status === "running")}
          data-testid="backfill-gaze-btn"
          onClick={async () => {
            // Pull the actual eligible count BEFORE showing the dialog so
            // the message isn't hard-coded to "350 candidates" — the user
            // saw "300" on a 48-candidate test which was clearly wrong.
            let queueable = 0;
            try {
              const { data: probe } = await adminApi().get(
                `/admin/gaze-backfill/probe?test_id=${testId}&overwrite=${gazeOverwrite}`
              );
              queueable = probe.queueable || 0;
            } catch { /* fall through with 0; the server will tell the truth */ }
            const minutes = Math.max(1, Math.ceil((queueable * 6) / 60 / 2));  // ~6s avg, 2 in parallel
            if (!window.confirm(
              `Run eye-movement / off-screen-gaze analysis on every webcam recording for THIS test?\n\n` +
              `• ${queueable} attempt(s) eligible (${gazeOverwrite ? "overwrite mode" : "skipping already-analysed"}).\n` +
              `• Estimated runtime: ~${minutes} minute(s) at 2 concurrent + 90s/attempt timeout.\n` +
              `• Runs as a background task — feel free to navigate away.\n` +
              `• Attempts with off-screen % ≥ 60% will get the 50% AI-score cap applied automatically.\n` +
              `\nStart backfill?`
            )) return;
            try {
              setGazeBackfilling(true);
              const { data: res } = await adminApi().post(
                `/admin/gaze-backfill?test_id=${testId}&overwrite=${gazeOverwrite}`
              );
              if (res.code === "already_running") {
                toast.info("A gaze backfill is already running. Polling for progress…");
              } else {
                toast.success(`Backfill queued for ${res.queued} attempt(s). Progress will appear here.`, { duration: 6000 });
              }
            } catch (e) {
              toast.error("Backfill failed: " + (e?.response?.data?.detail || e.message));
            } finally {
              setGazeBackfilling(false);
            }
          }}
        >
          {gazeProgress?.status === "running" ? "Running…" : "Run gaze backfill"}
        </Button>
        </div>
      </div>

      {/* Screen-share AI re-analysis — re-runs on captured frames, else extracts
          frames from the screen recording. Works on completed/old tests too. */}
      <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-sm" data-testid="screen-scan-backfill-bar">
        <div className="text-xs text-slate-700 leading-snug">
          <b className="text-purple-800">Screen-share analysis (open apps / remote-control tools)</b> runs live during the test. Re-run it on completed attempts — it re-uses captured frames or extracts them from the screen recording. {screenOverwrite ? "Overwrite ON — re-analyses every attempt." : "Skips attempts already analysed."}
          {(screenProgress?.sharing > 0) && (
            <span className="block mt-1 font-mono font-bold text-red-700" data-testid="screen-scan-detection-counter">
              ⚠ {screenProgress.sharing} screen-sharing detection(s) found{screenProgress?.status === "running" ? " so far" : ""}.
            </span>
          )}
          {screenProgress?.status === "running" && (
            <span className="block mt-1 font-mono text-purple-700" data-testid="screen-scan-backfill-progress">
              Processing <b>{screenProgress.processed}/{screenProgress.total}</b>… (done {screenProgress.succeeded} · skipped {screenProgress.skipped} · failed {screenProgress.failed})
              {screenProgress.stale && <span className="ml-2 text-red-600 font-bold">⚠ stalled — use Force reset</span>}
              {screenProgress.test_id !== testId && (
                <span className="ml-2 text-slate-500 italic text-[11px]">(running for {screenProgress.scope || "another test"})</span>
              )}
            </span>
          )}
          {screenProgress?.status === "done" && screenProgress.test_id === testId && (
            <span className="block mt-1 font-mono text-emerald-700" data-testid="screen-scan-last-run">
              Last run {screenProgress.finished_at ? relTime(screenProgress.finished_at) : ""}: {screenProgress.succeeded} done, {screenProgress.skipped} skipped, {screenProgress.failed} failed, {screenProgress.sharing} screen-sharing detection(s).
            </span>
          )}
          {screenProgress?.status === "cancelled" && (
            <span className="block mt-1 font-mono text-amber-700">Cancelled at {screenProgress.processed}/{screenProgress.total} ({screenProgress.sharing} detection(s)).</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {screenProgress?.status === "running" && (
            <Button size="sm" variant="outline" className="rounded-sm border-red-400 text-red-700 hover:bg-red-50" data-testid="cancel-screen-scan-btn"
              onClick={async () => {
                if (!window.confirm("Cancel the running screen-share analysis? Finished attempts keep their results.")) return;
                try { await adminApi().post("/admin/screen-scan-backfill/cancel"); toast.info("Cancel requested."); }
                catch (e) { toast.error("Cancel failed"); }
              }}>Cancel</Button>
          )}
          {(screenProgress?.status === "running") && (
            <Button size="sm" variant="outline" className="rounded-sm border-slate-400 text-slate-600 hover:bg-slate-100" data-testid="reset-screen-scan-btn"
              title="Clear the screen-scan job state. Use only if the job is genuinely stuck."
              onClick={async () => {
                if (!window.confirm("Force-reset the screen-scan analysis state? Use only if it's stuck.")) return;
                try { await adminApi().post("/admin/screen-scan-backfill/reset"); toast.success("State reset."); }
                catch (e) { toast.error("Reset failed"); }
              }}>Force reset</Button>
          )}
          <label className="flex items-center gap-1.5 text-[11px] font-mono text-slate-600 cursor-pointer select-none" title="Also re-analyse attempts already analysed.">
            <input type="checkbox" checked={screenOverwrite} onChange={(e) => setScreenOverwrite(e.target.checked)} className="accent-purple-600" data-testid="screen-scan-overwrite-toggle" />
            Re-analyse all (overwrite)
          </label>
          <Button size="sm" variant="outline" className="rounded-sm border-purple-400 text-purple-800 hover:bg-purple-100"
            disabled={screenBackfilling || (screenProgress?.status === "running")}
            data-testid="run-screen-scan-backfill-btn"
            onClick={async () => {
              if (!window.confirm(
                `Run AI screen-share analysis on every attempt for THIS test?\n\n` +
                `• Re-uses captured frames, or extracts frames from the screen recording.\n` +
                `• Runs as a background task — feel free to navigate away.\n` +
                `• Uses your LLM key (one GPT-4o vision call per sampled frame).\n\nStart analysis?`
              )) return;
              try {
                setScreenBackfilling(true);
                const { data: res } = await adminApi().post(`/admin/screen-scan-backfill?test_id=${testId}&overwrite=${screenOverwrite}`);
                if (res.code === "already_running") toast.info("A screen-scan analysis is already running.");
                else toast.success(`Screen-share analysis queued for ${res.queued} attempt(s).`, { duration: 6000 });
              } catch (e) {
                toast.error("Failed: " + (e?.response?.data?.detail || e.message));
              } finally { setScreenBackfilling(false); }
            }}>
            {screenProgress?.status === "running" ? "Running…" : "Run screen-share analysis"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="ranksheet-table">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <Overline>Video Assessment for Additional Screening — Rank Sheet {batch !== "all" && <span className="text-[#2563EB]">· {batch}</span>}</Overline>
            {data.is_combined ? (
              <>
                <div className="font-display font-black text-[#0A2540] text-2xl tracking-tight mt-1">
                  Ranking by Combined Score <span className="text-base font-mono text-[#2563EB]">(100% AI · MCQ {data.mcq_weight}% + Oral {data.oral_weight}%)</span>
                </div>
                <div className="text-xs font-mono text-slate-500 mt-1">
                  Combined exams are scored 100% by AI — no human grading. Disqualified candidates forced to bottom.
                </div>
              </>
            ) : data.has_human_scores ? (
              <>
                <div className="font-display font-black text-[#0A2540] text-2xl tracking-tight mt-1">
                  Formula: Final = AI × 0.4 + Human × 0.6
                </div>
                <div className="text-xs font-mono text-slate-500 mt-1">
                  Rank sorted by combined final score · Disqualified candidates forced to bottom.
                </div>
              </>
            ) : (
              <>
                <div className="font-display font-black text-[#0A2540] text-2xl tracking-tight mt-1">
                  Ranking by AI Score <span className="text-base font-mono text-amber-600">(Human grading pending)</span>
                </div>
                <div className="text-xs font-mono text-slate-500 mt-1">
                  Ranks will update to Final = AI × 0.4 + Human × 0.6 once human grading is complete.
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-[11px] font-mono text-slate-600 cursor-pointer select-none" title="Show only candidates flagged for screen-sharing or partial screen share.">
              <input type="checkbox" checked={onlyScreenShare} onChange={(e) => setOnlyScreenShare(e.target.checked)} className="accent-red-600" data-testid="filter-screen-share-toggle" />
              Screen-sharing only
            </label>
            {loading && <span className="text-xs font-mono text-slate-400">Loading…</span>}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16 font-mono">Rank</TableHead>
              <TableHead>Candidate</TableHead>
              <TableHead className="font-mono">AI</TableHead>
              <TableHead className="font-mono">Human</TableHead>
              <TableHead className="font-mono">
                {data.has_human_scores ? "Final" : "Score (AI)"}
              </TableHead>
              <TableHead className="font-mono">Max</TableHead>
              {data.is_combined && (
                <>
                  <TableHead className="font-mono" title="MCQ section score">MCQ</TableHead>
                  <TableHead className="font-mono" title="Oral section score">Oral</TableHead>
                  <TableHead className="font-mono" title={`Combined = MCQ% × ${data.mcq_weight}/100 + Oral% × ${data.oral_weight}/100`}>Combined</TableHead>
                  <TableHead className="font-mono" title="MCQ-phase proctoring integrity (100 = clean)">MCQ Proct</TableHead>
                  <TableHead className="font-mono" title="Oral-phase proctoring integrity (100 = clean)">Oral Proct</TableHead>
                  <TableHead className="font-mono" title="Gaze integrity score (100 = fully on-screen)">Gaze Score</TableHead>
                </>
              )}
              <TableHead
                className="font-mono cursor-pointer select-none hover:bg-slate-50"
                title="Click to sort by AI-content %. ≥60% triggers 50% score cap per question."
                onClick={() => setAiSort(aiSort === "desc" ? "asc" : aiSort === "asc" ? null : "desc")}
                data-testid="rank-ai-sort-toggle"
              >
                Avg AI %
                <span className="ml-1 text-[10px] inline-block w-3">
                  {aiSort === "desc" ? "▼" : aiSort === "asc" ? "▲" : ""}
                </span>
              </TableHead>
              <TableHead
                className="font-mono cursor-pointer select-none hover:bg-slate-50"
                title="Off-screen gaze % from eye-movement analysis. Click to sort. ≥60% triggers 50% score cap."
                onClick={() => setGazeSort(gazeSort === "desc" ? "asc" : gazeSort === "asc" ? null : "desc")}
                data-testid="rank-gaze-sort-toggle"
              >
                Gaze %
                <span className="ml-1 text-[10px] inline-block w-3">
                  {gazeSort === "desc" ? "▼" : gazeSort === "asc" ? "▲" : ""}
                </span>
              </TableHead>
              <TableHead className="font-mono" title="AI screen-scan result: remote-control / screen-sharing software detected, or partial screen share.">Screen-Sharing</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows
              .filter((r) => {
                if (onlyScreenShare && !(r.screen_share_detected || r.partial_screen_share)) return false;
                if (!searchQ) return true;
                const q = searchQ.toLowerCase();
                return (
                  (r.student_name || "").toLowerCase().includes(q) ||
                  (r.email || "").toLowerCase().includes(q) ||
                  (r.batch || "").toLowerCase().includes(q)
                );
              })
              .slice() // copy before sort so we don't mutate ranksheet state
              .sort((a, b) => {
                // Gaze sort takes precedence if set; otherwise AI sort.
                const sort = gazeSort || aiSort;
                const key = gazeSort ? "gaze_off_screen_pct" : "avg_ai_content_score";
                if (!sort) return 0;  // preserve server-side rank order
                const va = a[key];
                const vb = b[key];
                // null values always at the bottom, regardless of direction
                if (va == null && vb == null) return 0;
                if (va == null) return 1;
                if (vb == null) return -1;
                return sort === "desc" ? vb - va : va - vb;
              })
              .map((r) => (
              <TableRow
                key={r.attempt_id}
                data-testid={`rank-row-${r.attempt_id}`}
                className={r.disqualified ? "bg-red-50 hover:bg-red-100" : ""}
              >
                <TableCell className="font-mono font-black">
                  {r.disqualified
                    ? <Badge className="rounded-sm bg-red-600 text-white" data-testid={`dq-badge-${r.attempt_id}`}>DQ</Badge>
                    : (r.rank ? <span className="text-[#0A2540]">#{r.rank}</span> : "—")}
                </TableCell>
                <TableCell>
                  <div className={`font-bold ${r.disqualified ? "text-red-900 line-through" : "text-[#0A2540]"}`}>
                    {r.student_name}
                  </div>
                  <div className="text-xs font-mono text-slate-500">{r.email}</div>
                  {r.disqualified && r.disqualified_reason && (
                    <div className="mt-1 text-xs font-mono text-red-700">
                      Reason: <i>{r.disqualified_reason}</i>
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono">{r.ai_score ?? "—"}</TableCell>
                <TableCell className="font-mono">{r.human_score ?? "—"}</TableCell>
                <TableCell className={`font-mono font-black ${r.disqualified ? "text-red-700" : "text-[#2563EB]"}`}>
                  {data.is_combined ? "—" : (r.disqualified ? "—" : (r.final_score ?? r.effective_score ?? "—"))}
                </TableCell>
                <TableCell className="font-mono text-slate-500">{r.max_possible}</TableCell>
                {data.is_combined && (
                  <>
                    <TableCell className="font-mono" data-testid={`rank-mcq-${r.attempt_id}`}>
                      {r.mcq_score == null ? "—" : (
                        <span title={r.mcq_voice_penalty > 0 ? `Raw ${r.mcq_score_raw} − ${r.mcq_voice_penalty} (${r.mcq_voice_violations} secondary-voice in MCQ)` : undefined}>
                          {r.mcq_score}<span className="text-slate-400">/{r.mcq_max || 0}</span>
                          {r.mcq_voice_penalty > 0 && (
                            <span className="ml-1 text-[10px] text-red-600 font-bold" data-testid={`rank-mcq-voicepen-${r.attempt_id}`}>−{r.mcq_voice_penalty} voice</span>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono" data-testid={`rank-oral-${r.attempt_id}`}>
                      {r.oral_score == null ? "—" : <>{r.oral_score}<span className="text-slate-400">/{r.oral_max || 0}</span></>}
                    </TableCell>
                    <TableCell className={`font-mono font-black ${r.disqualified ? "text-red-700" : "text-[#2563EB]"}`} data-testid={`rank-combined-${r.attempt_id}`}>
                      {r.disqualified ? "—" : (r.combined_score != null ? `${r.combined_score}%` : "—")}
                    </TableCell>
                    <TableCell className="font-mono">{r.mcq_proctoring_score ?? "—"}</TableCell>
                    <TableCell className="font-mono">{r.oral_proctoring_score ?? "—"}</TableCell>
                    <TableCell className="font-mono">{r.gaze_score ?? "—"}</TableCell>
                  </>
                )}
                <TableCell className="font-mono" data-testid={`rank-ai-pct-${r.attempt_id}`}>
                  {r.avg_ai_content_score == null ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <span
                      className={
                        r.avg_ai_content_score >= 60 ? "text-red-600 font-bold" :
                        r.avg_ai_content_score >= 40 ? "text-amber-600 font-bold" :
                        "text-emerald-700"
                      }
                      title={`Average across ${r.ai_content_answer_count} answered question${r.ai_content_answer_count === 1 ? "" : "s"}. ≥60% triggers 50% score cap per question.`}
                    >
                      {r.avg_ai_content_score}%
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono" data-testid={`rank-gaze-pct-${r.attempt_id}`}>
                  {r.gaze_off_screen_pct == null ? (
                    <span className="text-slate-400" title={
                      r.gaze_analysis_status === "skipped" ? "Skipped (no recording)" :
                      r.gaze_analysis_status === "failed" ? "Analysis failed" :
                      "Not analysed yet — run gaze backfill"
                    }>—</span>
                  ) : (
                    <span
                      className={
                        r.gaze_off_screen_pct >= 60 ? "text-red-600 font-bold" :
                        r.gaze_off_screen_pct >= 30 ? "text-amber-600 font-bold" :
                        "text-emerald-700"
                      }
                      title="Off-screen gaze % from server-side eye-movement analysis. ≥60% triggers 50% score cap per question."
                    >
                      {r.gaze_off_screen_pct}%
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono" data-testid={`rank-screen-share-${r.attempt_id}`}>
                  {r.screen_share_detected ? (
                    <Badge className="rounded-sm bg-red-600 text-white" title="Screen-sharing / remote-control software detected on the shared screen.">
                      Sharing ×{r.screen_share_detected}
                    </Badge>
                  ) : r.partial_screen_share ? (
                    <Badge className="rounded-sm bg-amber-500 text-white" title="Candidate did not share their entire screen.">Partial</Badge>
                  ) : (
                    <span className="text-emerald-700 text-xs" title="No screen-sharing software detected.">Clean</span>
                  )}
                </TableCell>
                <TableCell>
                  {r.disqualified
                    ? <Badge className="rounded-sm bg-red-600 text-white">DISQUALIFIED</Badge>
                    : <Badge className={`rounded-sm ${r.status === "graded" ? "bg-[#0A2540] text-white" : "bg-slate-200 text-slate-700"}`}>{r.status}</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    {/* Direct-to-grade jump — saves the 3-click drill-down
                        (Attempts → filter → click Grade) on every row. */}
                    <Button
                      size="sm"
                      className={`rounded-sm text-xs ${r.status === "graded" ? "bg-slate-200 text-slate-700 hover:bg-slate-300" : "bg-[#0A2540] text-white hover:bg-[#0A2540]/90"}`}
                      onClick={() => nav(`/admin/interview/grade/${r.attempt_id}`)}
                      data-testid={`rank-grade-btn-${r.attempt_id}`}
                    >
                      <Bot size={12} className="mr-1" /> {r.status === "graded" ? "Re-grade" : "Grade"}
                    </Button>
                    {r.disqualified ? (
                      <Button
                        size="sm" variant="outline"
                        className="rounded-sm border-slate-300 text-xs"
                        onClick={() => reinstate(r)}
                        data-testid={`reinstate-btn-${r.attempt_id}`}
                      >
                        Reinstate
                      </Button>
                    ) : (
                      <Button
                        size="sm" variant="outline"
                        className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 text-xs"
                        onClick={() => disqualify(r)}
                        data-testid={`disqualify-btn-${r.attempt_id}`}
                      >
                        <Ban size={12} className="mr-1" /> Disqualify
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      </div>
      )}
    </div>
  );
}

function RankToolbar({ batch, setBatch, batches, searchQ, setSearchQ, onExportXlsx, onExportPdf, disabled }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-sm p-3">
      <div className="flex flex-wrap items-center gap-3">
        {typeof setSearchQ === "function" && (
          <Input
            placeholder="Search candidate, email, batch…"
            value={searchQ || ""}
            onChange={(e) => setSearchQ(e.target.value)}
            className="w-64 rounded-sm font-mono"
            data-testid="ranksheet-search-input"
          />
        )}
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 font-mono">Batch</span>
        <Select value={batch} onValueChange={setBatch}>
          <SelectTrigger className="w-56 rounded-sm font-mono" data-testid="ranksheet-batch-select">
            <SelectValue placeholder="All batches" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All batches</SelectItem>
            {batches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline" size="sm" className="rounded-sm"
          onClick={onExportXlsx}
          disabled={disabled}
          data-testid="ranksheet-export-xlsx"
        >
          <FileSpreadsheet size={14} className="mr-1.5" /> Excel
        </Button>
        <Button
          variant="outline" size="sm" className="rounded-sm"
          onClick={onExportPdf}
          disabled={disabled}
          data-testid="ranksheet-export-pdf"
        >
          <FileText size={14} className="mr-1.5" /> PDF
        </Button>
      </div>
    </div>
  );
}

function RankViewToggle({ view, setView }) {
  const Btn = ({ id, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => setView(id)}
      data-testid={`rank-view-${id}`}
      className={`flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-bold tracking-wide transition-colors ${
        view === id ? "bg-[#0A2540] text-white" : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  );
  return (
    <div className="flex items-center gap-2" data-testid="rank-view-toggle">
      <Btn id="detailed" icon={ListChecks} label="Detailed Sheet" />
      <Btn id="screening" icon={ClipboardList} label="Screening Report" />
    </div>
  );
}

function ScoreLegend({ isCombined }) {
  const [open, setOpen] = useState(false);
  const items = isCombined
    ? [["Combined %", "MCQ% × MCQ-weight + Oral% × Oral-weight (per-test weights). The ranking score after the adjustments below."]]
    : [["Score", "AI × 0.4 + Human × 0.6 once both are graded; AI-only until human grading is done."]];
  items.push(
    ["Integrity penalty", "Final × (1 − min(20%, severity_sum × 1.5%)). Flags add low=1 / medium=2 / high=3 to severity_sum, so violations lower the rank."],
    ["MCQ / Oral Proctoring (0–100)", "Cleanliness = 100 − min(100, severity_sum × 5). Higher = cleaner."],
    ["Gaze-Off %", "Share of webcam frames the candidate looked away from screen."],
    ["Gaze cap", "If Gaze-Off % ≥ 60 → Combined hard-capped at 50 (likely reading from a second device)."],
    ["Gaze Score", "100 − Gaze-Off %. Higher = more eyes-on-screen."],
    ["Oral answer-quality %", "AI grader's CONTENT-quality rating of the spoken answers (relevance/correctness), independent of marks."],
    ["Integrity Status", "Clean · Clean – Minor Notes (some flags / gaze ≥30%) · Excluded (gaze ≥60% or proctoring <50) · Disqualified (examiner)."],
  );
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm" data-testid="score-legend">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        data-testid="score-legend-toggle"
      >
        <span className="flex items-center gap-2 text-xs font-bold tracking-wide text-[#0A2540]">
          <Info size={14} className="text-[#2563EB]" /> How scores are calculated
        </span>
        {open ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 grid sm:grid-cols-2 gap-x-8 gap-y-2" data-testid="score-legend-body">
          {items.map(([term, detail]) => (
            <div key={term} className="text-[11px] leading-snug">
              <span className="font-bold text-[#0A2540]">{term}</span>
              <span className="text-slate-600"> — {detail}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScreeningStatusBadge({ status }) {
  const map = {
    "Clean": "bg-emerald-100 text-emerald-800",
    "Clean – Minor Notes": "bg-amber-100 text-amber-800",
    "Excluded": "bg-red-100 text-red-700",
    "Disqualified": "bg-red-600 text-white",
  };
  return <Badge className={`rounded-sm whitespace-nowrap ${map[status] || "bg-slate-200 text-slate-700"}`}>{status}</Badge>;
}

function ScreeningReport({ testId, batch }) {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const qs = batch && batch !== "all" ? `?batch=${encodeURIComponent(batch)}` : "";
        const { data } = await adminApi().get(`/admin/tests/${testId}/screening-report${qs}`);
        if (alive) setRep(data);
      } catch (e) {
        if (alive) toast.error("Failed to load screening report");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [testId, batch]);

  if (loading) {
    return <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-sm text-slate-500" data-testid="screening-loading">Building screening report…</div>;
  }
  if (!rep || !rep.rows?.length) {
    return <EmptyState title="Screening report empty" desc="Submitted & evaluated attempts will appear here." />;
  }
  const rowCls = (s) =>
    s === "Excluded" || s === "Disqualified" ? "bg-red-50/60"
    : s === "Clean – Minor Notes" ? "bg-amber-50/50" : "";
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="screening-table">
      <div className="p-5 border-b border-slate-200">
        <Overline>Screening Report — Integrity Analysis &amp; Ranking {batch !== "all" && <span className="text-[#2563EB]">· {batch}</span>}</Overline>
        <div className="font-display font-black text-[#0A2540] text-2xl tracking-tight mt-1">{rep.test_title}</div>
        <p className="text-slate-500 text-xs mt-1">{rep.rows.length} candidate(s) · ranked by {rep.score_label} · scores summarised with proctoring remarks below.</p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-14">Rank</TableHead>
            <TableHead>Candidate</TableHead>
            <TableHead>Email</TableHead>
            <TableHead className="w-28">{rep.score_label}</TableHead>
            <TableHead className="w-44">Integrity Status</TableHead>
            <TableHead>Remarks</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rep.rows.map((r, i) => (
            <TableRow key={i} className={rowCls(r["Integrity Status"])} data-testid={`screening-row-${i}`}>
              <TableCell className="font-mono font-bold">{r.Rank}</TableCell>
              <TableCell className="font-semibold text-[#0A2540]">{r.Candidate}</TableCell>
              <TableCell className="font-mono text-[11px] text-slate-500">{r.Email}</TableCell>
              <TableCell className="font-mono font-bold">{r.Score}</TableCell>
              <TableCell><ScreeningStatusBadge status={r["Integrity Status"]} /></TableCell>
              <TableCell className="text-[12px] text-slate-700 leading-snug max-w-md">{r.Remarks}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EmptyState({ title, desc }) {
  return (
    <div className="mt-6 border border-dashed border-slate-300 bg-white rounded-sm p-16 text-center">
      <div className="font-display font-bold text-[#0A2540] text-xl tracking-tight">{title}</div>
      <p className="text-slate-500 text-sm mt-2">{desc}</p>
    </div>
  );
}

function LiveMonitorPanel({ testId }) {
  const [data, setData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [transport, setTransport] = useState("connecting"); // 'websocket' | 'polling' | 'connecting'
  const wsRef = useRef(null);
  const pollIvRef = useRef(null);

  useEffect(() => {
    if (!testId) return;
    let alive = true;

    const startPolling = () => {
      if (pollIvRef.current) return;
      setTransport("polling");
      const fetchData = async () => {
        try {
          const { data } = await adminApi().get(`/admin/tests/${testId}/live-progress`);
          if (alive) { setData(data); setLastUpdate(new Date()); }
        } catch (e) {}
      };
      fetchData();
      pollIvRef.current = setInterval(fetchData, 4000);
    };

    const stopPolling = () => {
      if (pollIvRef.current) { clearInterval(pollIvRef.current); pollIvRef.current = null; }
    };

    if (!autoRefresh) {
      stopPolling();
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) {} wsRef.current = null; }
      return () => { alive = false; };
    }

    // Try WebSocket first
    const token = localStorage.getItem("himt_admin_token");
    if (!token) {
      startPolling();
      return () => { alive = false; stopPolling(); };
    }

    try {
      const wsBase = API.replace(/^http/, "ws").replace(/\/api$/, "");
      const wsUrl = `${wsBase}/api/ws/admin/live-progress/${testId}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      setTransport("connecting");

      const fallbackTimer = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          try { ws.close(); } catch (e) {}
          if (alive) startPolling();
        }
      }, 4000);

      ws.onopen = () => { setTransport("websocket"); };
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (alive) { setData(payload); setLastUpdate(new Date()); }
        } catch (e) {}
      };
      ws.onerror = () => {};
      ws.onclose = () => {
        clearTimeout(fallbackTimer);
        if (alive && autoRefresh) startPolling();
      };

      return () => {
        alive = false;
        clearTimeout(fallbackTimer);
        try { ws.close(); } catch (e) {}
        wsRef.current = null;
        stopPolling();
      };
    } catch (e) {
      startPolling();
      return () => { alive = false; stopPolling(); };
    }
  }, [testId, autoRefresh]);

  if (!testId) return <EmptyState title="Select a test" desc="Pick an active test above." />;
  if (!data) return <div className="p-10 text-slate-500">Loading live monitor…</div>;

  const rows = data.rows || [];
  const active = rows.filter((r) => r.status === "in_progress");
  const grading = rows.filter((r) => r.status === "grading");
  const submitted = rows.filter((r) => r.status === "submitted");

  return (
    <div className="mt-6 space-y-5" data-testid="live-monitor-panel">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge className="rounded-sm bg-[#E11D48] text-white" data-testid="live-active-badge">
            <span className="h-2 w-2 rounded-full bg-white recording-pulse mr-1.5 inline-block" />
            {active.length} Live Now
          </Badge>
          <Badge className="rounded-sm bg-[#2563EB] text-white">{grading.length} Grading</Badge>
          <Badge className="rounded-sm bg-slate-200 text-slate-700 hover:bg-slate-200">{submitted.length} Submitted</Badge>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono text-slate-500">
          {transport === "websocket" && (
            <span className="flex items-center gap-1 text-emerald-600" data-testid="live-transport-ws">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 recording-pulse inline-block" />
              WS · Real-time
            </span>
          )}
          {transport === "polling" && (
            <span className="text-amber-600" data-testid="live-transport-poll">Polling · 4s</span>
          )}
          {transport === "connecting" && (
            <span className="text-slate-400" data-testid="live-transport-connecting">Connecting…</span>
          )}
          {lastUpdate && <span>Updated {fmtISTTime(lastUpdate.toISOString())} IST</span>}
          <Button variant="outline" size="sm" onClick={() => setAutoRefresh(!autoRefresh)} className="rounded-sm" data-testid="toggle-autorefresh-btn">
            {autoRefresh ? "Pause" : "Resume"} Auto-refresh
          </Button>
        </div>
      </div>

      {/* Live active cards */}
      {active.length > 0 && (
        <div>
          <Overline>Currently Taking Test</Overline>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
            {active.map((r) => (
              <LiveCard key={r.attempt_id} row={r} />
            ))}
          </div>
        </div>
      )}

      {/* Grading */}
      {grading.length > 0 && (
        <div>
          <Overline>Background Grading</Overline>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
            {grading.map((r) => (
              <LiveCard key={r.attempt_id} row={r} />
            ))}
          </div>
        </div>
      )}

      {active.length === 0 && grading.length === 0 && (
        <EmptyState title="No live activity" desc="When students start taking the test, they will appear here in real-time." />
      )}

      {/* Recently submitted */}
      {submitted.length > 0 && (
        <div>
          <Overline>Recently Submitted</Overline>
          <div className="mt-3 bg-white border border-slate-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Batch</TableHead>
                  <TableHead className="font-mono">Answered</TableHead>
                  <TableHead className="font-mono">Flags</TableHead>
                  <TableHead>Submitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submitted.slice(0, 10).map((r) => (
                  <TableRow key={r.attempt_id}>
                    <TableCell className="font-semibold text-[#0A2540]">{r.student_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.batch || "—"}</TableCell>
                    <TableCell className="font-mono">{r.answered_count}/{r.total_questions}</TableCell>
                    <TableCell className="font-mono">{r.proctoring_flags_count}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.last_activity_at ? fmtISTTime(r.last_activity_at) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

function LiveCard({ row }) {
  const progress = row.total_questions
    ? Math.round(((row.answered_count || 0) / row.total_questions) * 100)
    : 0;
  const isStalled = row.seconds_since_activity > 30;
  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };
  const statusTone =
    row.disqualified ? "border-red-500 bg-red-50" :
    row.status === "in_progress" ? "border-[#E11D48]" :
    row.status === "grading" ? "border-[#2563EB]" :
    "border-slate-200";
  return (
    <div className={`bg-white border-2 rounded-sm p-4 ${statusTone}`} data-testid={`live-card-${row.attempt_id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`font-bold ${row.disqualified ? "text-red-900" : "text-[#0A2540]"}`}>
            {row.student_name}
            {row.disqualified && (
              <Badge
                className="ml-2 rounded-sm bg-red-600 text-white text-[10px] align-middle"
                data-testid={`live-dq-badge-${row.attempt_id}`}
              >
                DQ
              </Badge>
            )}
          </div>
          <div className="text-xs font-mono text-slate-500 mt-0.5">{row.email}</div>
          {row.batch && <Badge variant="outline" className="rounded-sm font-mono text-[10px] mt-1.5">{row.batch}</Badge>}
          {row.disqualified && row.disqualified_reason && (
            <div className="mt-1 text-[11px] font-mono text-red-700 leading-snug" data-testid={`live-dq-reason-${row.attempt_id}`}>
              <i>Reason: {row.disqualified_reason}</i>
            </div>
          )}
        </div>
        {row.status === "in_progress" && (
          <div className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-red-500 recording-pulse" />
            <span className="text-[10px] tracking-[0.2em] font-bold text-red-600 font-mono">LIVE</span>
          </div>
        )}
        {row.status === "grading" && (
          <Badge className="rounded-sm bg-[#2563EB] text-white text-[10px]">GRADING</Badge>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex justify-between text-xs font-mono">
          <span className="text-slate-500">Progress</span>
          <span className="font-bold">{row.answered_count}/{row.total_questions}</span>
        </div>
        <Progress value={progress} className="h-1.5 rounded-sm" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-xs font-mono">
        <div>
          <div className="text-slate-500 text-[10px] tracking-wide uppercase">Elapsed</div>
          <div className="font-bold text-[#0A2540]">{fmtTime(row.elapsed_seconds)}</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px] tracking-wide uppercase">Idle</div>
          <div className={`font-bold ${isStalled ? "text-red-600" : "text-[#0A2540]"}`}>{fmtTime(row.seconds_since_activity)}</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px] tracking-wide uppercase">Flags</div>
          <div className={`font-bold ${row.proctoring_flags_count > 3 ? "text-red-600" : "text-[#0A2540]"}`}>{row.proctoring_flags_count}</div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold">
        {row.id_verified
          ? <span className="text-green-600"><CheckCircle2 size={10} className="inline" /> ID Verified</span>
          : <span className="text-amber-600">ID Pending</span>}
        {isStalled && <span className="text-red-600">· Stalled</span>}
        {row.network && <NetQualityDot net={row.network} />}
      </div>
    </div>
  );
}

function NetQualityDot({ net }) {
  const q = net?.quality || "good";
  const palette = {
    good: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500", label: "Net OK" },
    fair: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500", label: "Net Fair" },
    poor: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500", label: "Net Poor" },
  }[q] || null;
  if (!palette) return null;
  const Icon = q === "poor" ? WifiOff : Wifi;
  const tooltip = `${palette.label}${net?.ping_ms != null ? ` · ${net.ping_ms}ms` : ""}${net?.downlink_mbps != null ? ` · ${net.downlink_mbps}Mbps` : ""}${net?.effective_type ? ` · ${net.effective_type}` : ""}`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${palette.bg} ${palette.text} ${palette.border} tracking-normal normal-case text-[10px] font-mono font-bold`}
      title={tooltip}
      data-testid={`net-quality-${q}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${palette.dot}`} />
      <Icon size={10} /> {palette.label}
    </span>
  );
}
